// Toronto, "Maple leaves and the CN Tower". A cold, clear October night on the Toronto Islands: the seawall promenade by
// the Centre Island ferry dock, looking north-north-west (about 348 degrees) across the Inner Harbour at the downtown
// skyline, 2 to 3 km off. Left to right, as seen from there (bearings from the dock): CityPlace's glass condos (-39),
// the Rogers Centre's dome (-29) at the foot of the CN Tower (-23, 2.35 km), the Southcore condos and Queens Quay
// Terminal on the water (-14 to -11), the financial district behind the waterfront condos (First Canadian Place -10,
// the TD towers, Commerce Court and Scotia Plaza -6, Brookfield and the gold Royal Bank Plaza -4), CIBC Square and
// Harbour Plaza by Union Station, the Westin Harbour Castle at the ferry terminal (-2), the L Tower (+2), SkyTower at
// Pinnacle One Yonge (+4, 352 m, Canada's tallest building since 2026, its top level with the CN Tower's pod), cranes on
// One Yonge's next towers and the East Bayfront (+6 to +12). Billy Bishop airport (-61) is off the frame to the left:
// the turboprops on final for runway 26 cross the harbour low, right to left, in front of the towers.
// The CN Tower is drawn to its real stages: 553 m to the antenna's tip, the main pod (335-368 m) at 0.64 of the height,
// the SkyPod at 447 m (0.81), the 96 m antenna (the top 0.17); it stands 2.25 times as tall as First Canadian Place
// from here (298 m, 2.9 km off). Its LEDs are a cool white most of the loop, with two timed shows (red and white, then
// a slow rainbow).
// The foreground is the island's side: the seawall's edge, a snack kiosk with its sign (the city's billboard), lamp
// posts, a bench, a green bin, a red maple and a willow dropping their leaves, Canada geese on the grass, and the people
// who came out to look at the view. Things that can't be seen from here: street life downtown, Casa Loma, Kensington
// Market (all behind the towers or kilometres inland).
// render(t, { crowd }) thins the people (see CROWD); crowd 0 draws nobody, and the kiosk stands unattended.
//
// The loop's timeline (seconds):
//   8-30      a turboprop on final for Billy Bishop crosses the harbour low, right to left, landing lights on
//   14-50     a dog walker crosses the promenade left to right; at 36-41 the dog barks at the geese, who hiss and flap
//   30-84     BIG: an island ferry leaves the Bay Street terminal and crosses the harbour to the west, lit, its decks
//             full, horn at 31; the kid waves at it (42-60) and the passengers wave back; its wash slaps the seawall
//             at 72-90 and the floating leaves and the geese on the water bob
//   52-70     a yellow water taxi crosses near the island, left to right
//   56-86     a raccoon comes out from under the willow, tries the green bin's lid, gives up and waddles back
//   62-74     a cyclist rides by, right to left, ringing her bell
//   90-112    the next turboprop lands
//   100-112   a jogger runs by, left to right
//   110-148   BIG: the CN Tower's lights rise in red from the foot, then red and white bands climb the shaft and the pod
//             glows red; she points at it (114-132), the photographer shoots it, and the couple kiss at 140-146
//   120-156   the dog walker comes back; the water taxi comes back at 134-152
//   140-190   a couple strolls by hand in hand, right to left
//   156-236   BIG: the geese on the grass look up and honk (156-162); a skein of Canada geese flies in from the east in a
//             V (160-184), circles down and lands on the harbour (184-189), then paddles off west (189-236)
//   186-208   the third turboprop lands
//   196-210   two cyclists ride by, left to right, bells at 197
//   206-230   the CN Tower's lights run a slow rainbow up the shaft; she points again
//   222-234   the jogger runs back
//   all loop  maple leaves fall at three depths and drift on the water, thin cloud drifts over the stars, windows switch
//             on and off, the red aircraft lamps on the CN Tower, SkyTower and the cranes pulse, cars and a red
//             streetcar run along Queens Quay, jets blink high overhead, the willow sways, fallen leaves tumble, the couple
//             lean together every 40 s, the photographer checks his shot every 24 s, the friends on the edge share a
//             box of butter tarts, the fisher casts and reels, the vendor wipes the counter and the urn steams
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 166;                                     // the far waterline: Queens Quay's seawall, everything across stands on it
const QY = 199;                                     // the island's seawall edge: the water ends above this row
const EDGE = 204;                                   // feet of the people standing at the edge
const LANE_A = 220, LANE_B = 230;                   // feet rows of the people crossing the promenade
const GRASS = 234;                                  // where the path gives way to the lawn
// The CN Tower: its axis, the antenna's tip, the antenna's foot (the SkyPod's roof), the SkyPod's floor, the main pod's
// top and bottom rows. 146 px for 553 m: 0.264 px a metre
const CN = { x: 178, top: 20, mast: 45, sky: 49, pod0: 69, pod1: 77 };
const DOME = { cx: 140, x0: 117, x1: 163, top: 143 };   // the Rogers Centre, its roof's crown
const SKYT = { x0: 406, x1: 415, top: 62 };          // SkyTower at Pinnacle One Yonge
const BB = { x: 68, y: 128, w: 46, h: 20 };          // the kiosk's sign (the city's billboard)
const KIOSK = { x0: 64, x1: 118, cx: 91, roof: 150, eave: 158, base: 214 };
const LAMPS = [{ x: 205, y: 150 }, { x: 374, y: 150 }];   // the promenade's lamp posts (the lantern's centre)
const BENCH = { x: 222, y: 204 };                   // a bench facing the water: its feet, seen from behind
const BIN = { x: 404, y: 215 };                     // a green bin by the willow (the raccoon's)
const MAPLE = { x: 30, base: 254 };                 // the red maple on the left
const WILLOW = { x: 452, base: 252 };               // the willow on the right
const MOON = { x: 438, y: 42 };
const ACCENT = '#e00000';

/* ================= Timeline ================= */
const PLANES = [{ t0: 8, t1: 30 }, { t0: 90, t1: 112 }, { t0: 186, t1: 208 }];
const FERRY = { t0: 30, t1: 84, horn: 31, wy: 187 };
const WASH = { t0: 72, t1: 90 };                    // the ferry's wash reaching the seawall
const TAXI = [{ t0: 52, t1: 70, dir: 1 }, { t0: 134, t1: 152, dir: -1 }];
const RACCOON = { t0: 56, t1: 86 };
const DOGW = [{ t0: 14, t1: 50, dir: 1, bark: [36, 41] }, { t0: 120, t1: 156, dir: -1, bark: null }];
const BIKES = [{ t0: 62, t1: 74, dir: -1, n: 1, bell: 64 }, { t0: 196, t1: 210, dir: 1, n: 2, bell: 197 }];
const JOG = [{ t0: 100, t1: 112, dir: 1 }, { t0: 222, t1: 234, dir: -1 }];
const STROLL = { t0: 140, t1: 190 };
const SHOW1 = { t0: 110, t1: 148 }, SHOW2 = { t0: 206, t1: 230 };
const POINTS = [[114, 132], [210, 222]], KISS = [140, 146], WAVE = [42, 60];
const GEESE = { look: 156, t0: 160, land: 184, down: 189, t1: 236 };
// The people: about half of a cast of 16 by default. render(t, { crowd }) shows those whose rank is under crowd; rank 0
// (the couple on the bench, the photographer, the vendor) stays while anybody does; crowd 0 draws nobody, and takes the
// tripod, the rod, the dog, the bikes and the tarts along. The geese, the raccoon and the boats stay.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const RANK = { couple: 0, photo: 0, vendor: 0, friends: 0.18, kid: 0.3, dog: 0.38, bike1: 0.44, fisher: 0.55, jog: 0.62,
  stroll: 0.7, phone: 0.78, bike2: 0.86 };

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const flipC = src => { const [c, x] = makeCanvas(src.width, src.height); x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0); return c; };
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** piecewise-linear script: keys [[t, x], ...] sorted by t; the position at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { x: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { x: keys[keys.length - 1][1], moving: false };
}
const inWin = (t, a, b) => t >= a && t < b;
/** the red aircraft lamps pulse slowly, all together (as a city's obstruction lights do): 0..1 */
const beacon = t => smooth(0.2, 0.85, 0.5 + 0.5 * wave(t, 80));

/* ================= The sky: clear and cold, the city's glow under it, thin cloud drifting over the stars ================= */
// No moon: the week of a new moon. The glow is warmest over downtown and fades to a deep navy overhead.
const SKY = ['#04071a', '#060a1e', '#090e24', '#0c122a', '#101731', '#151c38', '#1b213f', '#222645', '#2a2b4b', '#33304f',
  '#3e3653', '#4a3c56', '#584358', '#664b59'];
const skyV = (x, y) => Math.pow(y / WL, 1.5) * 0.62 + 0.26 * Math.exp(-(((x - 310) / 150) ** 2)) * smooth(60, WL, y)
  + 0.12 * Math.exp(-(((x - 120) / 110) ** 2)) * smooth(90, WL, y);
function renderSky() {
  const p = pix(W, WL);
  for (let y = 0; y < WL; y++) for (let x = 0; x < W; x++) p.set(x, y, pick(SKY, skyV(x, y) + (vnoise(x / 60, y / 9, 3) - 0.5) * 0.04, x, y));
  return p.done();
}
const hazeAt = (x, y) => SKY[Math.min(SKY.length - 1, Math.round(clamp(skyV(x, y), 0, 1) * (SKY.length - 1)))];
const STARS = [];
{
  const r = rng(77);
  while (STARS.length < 110) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 96);
    if (y > 70 && r() < 0.6) continue;                                                  // fewer down in the glow
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.35 + r() * r() * 0.65, c: r() < 0.2 ? '#ffe8c8' : r() < 0.4 ? '#cfdcff' : '#eef0ff' });
  }
}
/* thin cirrus, tileable (period W): long faint streaks lit from below by the city */
function makeWisps(seed, h, banks, col, colLit, a) {
  const r = rng(seed), streaks = [];
  for (let b = 0; b < banks; b++) {
    const bx = r() * W, by = 6 + r() * (h - 12), n = 3 + Math.floor(r() * 4);
    for (let i = 0; i < n; i++) streaks.push({ x: bx + (r() - 0.5) * 60, y: by + (r() - 0.5) * 8, rx: 24 + r() * 50, ry: 1.2 + r() * 2.2 });
  }
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const s of streaks) {
      let dx = x - s.x; dx -= Math.round(dx / W) * W;
      const q = 1 - (dx / s.rx) ** 2 - ((y - s.y) / s.ry) ** 2;
      if (q > v) v = q;
    }
    v *= 0.75 + 0.5 * vnoise(x / 9, y / 2, seed, Math.round(W / 9));
    if (v > 0.12 + bayer(x, y) * 0.5) p.set(x, y, v > 0.62 ? colLit : col, a);
  }
  return p.done();
}

/* ================= Windows that switch on and off ================= */
// Most lit windows are baked into the skyline; these few switch on and off, each on its own whole-number period.
const WINS = [];
function addTog(r, x, y, w, h, col) {
  WINS.push({ x, y, w, h, c: col, k: 2 + Math.floor(r() * 5), ph: r(), duty: 0.3 + r() * 0.45 });
}
const WARMW = ['#ffd890', '#f8c070', '#ffe6b0', '#f0b060'], COOLW = ['#e4ecf8', '#c8d8ec', '#d8e2f0'], BRONZE = '#d8a860';

/* ================= The skyline ================= */
// Each building: x0..x1 (x1 exclusive), its roof row, its stone or glass ramp (dark to light), how its windows light
// (floors: an office's lit floors; dots: a condo's windows; grid: a black tower's bronze lattice), its crown, and how
// much of the night's haze lies on it. The face to the left catches the glow of the west end; the right one is darker.
const GLASS_B = ['#0d1424', '#141d33', '#1c2842', '#263552', '#334566'];
const GLASS_G = ['#0b151e', '#12202b', '#1a2d38', '#243c48', '#304e5a'];
const MARBLE = ['#262a36', '#383d4c', '#505668', '#6c7284', '#9298aa'];
const RED_G = ['#1c0c10', '#2c1216', '#40191e', '#582326', '#743030'];
const STEEL = ['#181c26', '#242a36', '#323948', '#444e5c', '#5a6474'];
const BLACK = ['#07080b', '#0c0e12', '#121419', '#1a1c23', '#24272f'];
const CONDO = ['#121824', '#1c2432', '#283242', '#364254', '#485668'];
const PRECAST = ['#1c1a22', '#2a262e', '#3a343c', '#4e464e', '#665a60'];
const GOLDR = ['#2e2008', '#5a3e10', '#8c621a', '#bc8c2a', '#e2b648', '#fbe08a'];
function paintBldg(p, r, b) {
  const { x0, x1, top, ramp } = b, base = b.base ?? WL, haze = b.haze ?? 0.3, w = x1 - x0;
  const crownRows = b.crown === 'chevron' ? 6 : b.crown === 'slope' || b.crown === 'sail' ? Math.round(w * (b.steep ?? 0.8)) : 0;
  const roofAt = x => {                                                                   // the roof's row over column x (shaped crowns)
    const u = (x - x0 + 0.5) / w;
    if (b.crown === 'slope') return top + Math.round((b.dir > 0 ? 1 - u : u) * crownRows);
    if (b.crown === 'sail') return top + Math.round(Math.pow(b.dir > 0 ? 1 - u : u, 1.8) * crownRows);
    if (b.crown === 'chamfer') return top + (x === x0 || x === x1 - 1 ? 1 : 0);
    if (b.crown === 'chevron') { const m = Math.abs(u - 0.5) * 2; return top + Math.round(m * 4) + (m > 0.8 ? 2 : 0); }
    if (b.crown === 'step') return top + (u < 0.25 || u > 0.75 ? 3 : 0);
    return top;
  };
  for (let x = x0; x < x1; x++) {
    const rt = roofAt(x), face = x - x0 < w * 0.55 ? 1 : 0;
    for (let y = rt; y < base; y++) {
      let v = 0.36 + (face ? 0.14 : -0.04) + (vnoise(x / 3, y / 4, 11 + x0) - 0.5) * 0.08 + 0.12 * smooth(base - 40, base, y);
      if (x === x0) v += 0.22; else if (x === x1 - 1) v -= 0.18;
      if (y === rt) v += 0.25;
      let col = pick(ramp, v, x, y, 0.6);
      if (haze) col = mixHex(col, hazeAt(x, y), haze);
      p.set(x, y, col);
    }
  }
  // windows: lit ones baked in, some switching
  const lit = b.lit ?? 0.35, wc = b.cool ? COOLW : WARMW, inner = (x, y) => x > x0 && x < x1 - 1 && y > roofAt(x) + 1;
  if (b.win === 'floors') {
    for (let y = top + 3; y < base - 1; y += 2) {
      const v = r();
      if (v < lit) {                                                                        // a lit floor, whole or in part
        const a = v < lit * 0.55 ? x0 + 1 : x0 + 1 + Math.floor(r() * (w - 3)), e = v < lit * 0.55 ? x1 - 1 : Math.min(x1 - 1, a + 2 + Math.floor(r() * 4));
        const col = wc[Math.floor(r() * wc.length)];
        for (let x = a; x < e; x++) if (inner(x, y)) p.set(x, y, haze > 0.3 ? mixHex(col, hazeAt(x, y), haze * 0.7) : col);
      } else if (v < lit + 0.2 && w > 4) addTog(r, x0 + 1 + Math.floor(r() * (w - 4)), y, 2 + Math.floor(r() * 2), 1, wc[0]);
    }
  } else if (b.win === 'grid') {
    for (let y = top + 3; y < base - 1; y += 2) for (let x = x0 + 1; x < x1 - 1; x++) {
      const v = r();
      if (v < lit && inner(x, y)) p.set(x, y, mixHex(BRONZE, '#0c0e12', 0.25 + r() * 0.35));
      else if (v < lit + 0.02 && inner(x, y)) addTog(r, x, y, 1, 1, BRONZE);
    }
  } else if (b.win === 'dots') {
    for (let y = top + 3; y < base - 1; y += 2) for (let x = x0 + 1; x < x1 - 1; x += 1 + (r() < 0.5 ? 1 : 0)) {
      const v = r();
      if (!inner(x, y)) continue;
      if (v < lit) { const col = wc[Math.floor(r() * wc.length)]; p.set(x, y, haze > 0.3 ? mixHex(col, hazeAt(x, y), haze * 0.7) : col); }
      else if (v < lit + 0.13) addTog(r, x, y, 1, 1, wc[Math.floor(r() * wc.length)]);
    }
  }
  // crowns
  if (b.crown === 'lit') for (let y = top; y < top + 3; y++) for (let x = x0; x < x1; x++) p.set(x, y, y === top ? '#f4f8ff' : x === x0 ? '#c8d8f0' : '#9ab0d0');
  if (b.crown === 'band') for (let x = x0 + 1; x < x1 - 1; x++) p.set(x, top + 2, '#8aa0c0');
  if (b.crown === 'chevron') {                                                              // Scotia Plaza: the notched, stepped top, lit
    for (let x = x0; x < x1; x++) { const rt = roofAt(x); p.set(x, rt, '#e8a070'); if (x > x0 && x < x1 - 1) p.set(x, rt + 1, '#8a4030'); }
  }
  if (b.crown === 'slope' || b.crown === 'sail') for (let x = x0; x < x1; x++) p.set(x, roofAt(x), b.edgeCol ?? '#bcd0ec');
  if (b.crown === 'chamfer') for (let x = x0 + 1; x < x1 - 1; x++) { p.set(x, top, '#e8ecf4'); p.set(x, top + 1, '#b0b8c8'); }
  if (b.crown === 'fin') for (let y = top; y < base - 20; y++) p.set(x1 - 2, y, y < top + 30 ? '#c8dcf4' : '#8aa4c8');   // a lit edge up the corner
  if (b.crown === 'brace') {                                                                // CIBC Square: the diagonal grid on its glass
    for (let y = top + 4; y < base; y++) for (let x = x0 + 1; x < x1 - 1; x++) if ((x - x0 + y) % 8 === 0 || (x - x0 - y + 400) % 8 === 0) p.set(x, y, mixHex('#3a5470', hazeAt(x, y), 0.35));
    for (let x = x0; x < x1; x++) p.set(x, top, '#d8e6f8');
  }
  if (b.crown === 'step') for (let x = x0; x < x1; x++) p.set(x, roofAt(x), '#a8b8cc');
  if (b.beacon) b.beaconAt = [Math.round((x0 + x1 - 1) / 2), top - 1];
}
// the far towers of midtown, Yorkville and St. James Town, dim through the haze between the core's towers
function paintMidtown(p, r) {
  let x = 196;
  while (x < 446) {
    const w = 5 + Math.floor(r() * 9), top = 128 + Math.floor(r() * 24) - (x > 280 && x < 380 ? 8 : 0);
    for (let i = x; i < Math.min(446, x + w); i++) for (let y = top; y < WL; y++) {
      const v = 0.3 + (i === x ? 0.15 : 0) + 0.15 * smooth(top, WL, y);
      p.set(i, y, mixHex(pick(CONDO, v, i, y), hazeAt(i, y), 0.62));
      if (y > top + 2 && (y & 1) && r() < 0.12) p.set(i, y, mixHex(WARMW[Math.floor(r() * 4)], hazeAt(i, y), 0.55));
    }
    x += w + (r() < 0.3 ? 2 : 0);
  }
}
// the towers, back (north) to front (south); roofs from h/d, see the header
const BACK_TOWERS = [
  { n: 'scotia', x0: 318, x1: 330, top: 108, ramp: RED_G, win: 'floors', lit: 0.3, crown: 'chevron', haze: 0.22 },
  { n: 'fcp', x0: 276, x1: 287, top: 102, ramp: MARBLE, win: 'floors', lit: 0.42, cool: true, crown: 'chamfer', haze: 0.18, beacon: true },
  { n: 'ccw', x0: 306, x1: 316, top: 114, ramp: STEEL, win: 'floors', lit: 0.36, cool: true, crown: 'band', haze: 0.22 },
  { n: 'tdct', x0: 334, x1: 342, top: 106, ramp: GLASS_B, win: 'floors', lit: 0.3, cool: true, crown: 'step', haze: 0.22 },
  { n: 'tdbank', x0: 286, x1: 296, top: 116, ramp: BLACK, win: 'grid', lit: 0.5, haze: 0.16 },
  { n: 'tdnorth', x0: 266, x1: 275, top: 124, ramp: BLACK, win: 'grid', lit: 0.44, haze: 0.18 },
  { n: 'baywel', x0: 342, x1: 347, top: 119, ramp: GLASS_G, win: 'floors', lit: 0.3, cool: true, haze: 0.2 },
];
const MID_TOWERS = [
  { n: 'mls1', x0: 213, x1: 221, top: 124, ramp: CONDO, win: 'dots', lit: 0.3, crown: 'lit', haze: 0.2 },
  { n: 'mls2', x0: 223, x1: 231, top: 129, ramp: CONDO, win: 'dots', lit: 0.3, haze: 0.2 },
  { n: 'delta', x0: 236, x1: 246, top: 120, ramp: GLASS_B, win: 'dots', lit: 0.34, crown: 'band', haze: 0.18 },
  { n: 'ice2', x0: 248, x1: 256, top: 112, ramp: GLASS_B, win: 'dots', lit: 0.3, crown: 'slope', dir: 1, steep: 0.9, haze: 0.16, edgeCol: '#a8c8f0' },
  { n: 'ice1', x0: 257, x1: 265, top: 105, ramp: GLASS_B, win: 'dots', lit: 0.3, crown: 'slope', dir: -1, steep: 0.9, haze: 0.16, edgeCol: '#a8c8f0' },
  { n: 'tenyork', x0: 267, x1: 274, top: 106, ramp: GLASS_G, win: 'dots', lit: 0.3, crown: 'fin', haze: 0.16 },
  { n: 'hp1', x0: 324, x1: 330, top: 103, ramp: CONDO, win: 'dots', lit: 0.34, crown: 'lit', haze: 0.14 },
  { n: 'hp2', x0: 331, x1: 337, top: 107, ramp: CONDO, win: 'dots', lit: 0.34, crown: 'lit', haze: 0.14 },
  { n: 'cibc', x0: 358, x1: 367, top: 104, ramp: GLASS_B, win: 'floors', lit: 0.32, cool: true, crown: 'brace', haze: 0.14 },
  { n: 'ltower', x0: 384, x1: 393, top: 116, ramp: GLASS_G, win: 'dots', lit: 0.3, crown: 'sail', dir: 1, steep: 1.2, haze: 0.14, edgeCol: '#d8e8f8' },
  { n: 'pinn1', x0: 395, x1: 402, top: 121, ramp: CONDO, win: 'dots', lit: 0.36, crown: 'lit', haze: 0.14 },
];
// CityPlace: a crowd of glass condo towers west of the stadium, between the railway lands and the Gardiner
const CITYPLACE = [];
{
  const r = rng(404);
  let x = 2;
  while (x < 118) {
    const w = 6 + Math.floor(r() * 5), top = 118 + Math.floor(r() * 22) + (x > 96 ? 6 : 0);
    CITYPLACE.push({ n: 'cp', x0: x, x1: x + w, top, ramp: r() < 0.5 ? GLASS_G : GLASS_B, win: 'dots', lit: 0.3 + r() * 0.12, crown: r() < 0.3 ? 'lit' : r() < 0.4 ? 'slope' : 'flat', dir: r() < 0.5 ? 1 : -1, steep: 0.5, haze: 0.2 });
    x += w + 1 + Math.floor(r() * 3);
  }
}
// the waterfront's front row along Queens Quay, nearer and warmer: [x0, x1, roof, ramp, windows, lit share]
const FRONT = [
  [0, 14, 146, PRECAST, 'dots', 0.4], [14, 30, 140, CONDO, 'dots', 0.38], [30, 46, 148, PRECAST, 'dots', 0.4], [46, 62, 138, CONDO, 'dots', 0.36],
  [62, 84, 134, CONDO, 'dots', 0.36], [84, 104, 140, PRECAST, 'dots', 0.4], [104, 117, 147, PRECAST, 'dots', 0.4],
  [117, 135, 154, PRECAST, 'dots', 0.42], [135, 152, 156, PRECAST, 'dots', 0.42], [152, 166, 152, PRECAST, 'dots', 0.42],
  [166, 180, 136, CONDO, 'dots', 0.4], [182, 196, 133, CONDO, 'dots', 0.38], [196, 210, 141, PRECAST, 'dots', 0.4],
  [210, 228, 136, CONDO, 'dots', 0.38], [228, 238, 152, PRECAST, 'dots', 0.44],
  [262, 280, 139, CONDO, 'dots', 0.38], [280, 300, 144, PRECAST, 'dots', 0.4], [300, 312, 127, CONDO, 'dots', 0.36],
  [313, 324, 131, CONDO, 'dots', 0.36], [324, 340, 135, CONDO, 'dots', 0.36], [340, 357, 156, PRECAST, 'dots', 0.46],
  [382, 394, 143, CONDO, 'dots', 0.38], [394, 405, 147, PRECAST, 'dots', 0.4], [416, 428, 149, PRECAST, 'dots', 0.4], [428, 442, 137, PRECAST, 'floors', 0.4],
  [442, 458, 146, CONDO, 'dots', 0.38], [458, 480, 141, PRECAST, 'dots', 0.36],
];

/* ================= The CN Tower ================= */
// Concrete, floodlit by its own LEDs: a cool white by default (CN_PLAIN), a colour during the shows (CN_TINT). The
// shaft below the pod is the Y of three wings round a hexagonal core: seen from the south-south-east the left wing
// catches the light, the core shows a seam, the right wing falls into shade. The pod: the EdgeWalk's roof, two bands of
// windows (the restaurant, the look-out), the glass floor's rim and the white ring under it; the SkyPod a small lit drum.
const CN_PLAIN = ['#2a2e3c', '#444a5a', '#646a7c', '#8a90a2', '#b4b9c8', '#dcdfe8', '#f4f5f9'];
const POD_ROWS = [[3, 'cap'], [4, 'win'], [5, 'win'], [5, 'win2'], [5, 'rim'], [6, 'ring'], [6, 'ring'], [5, 'ring2'], [3, 'neck']];   // half-widths
const cnHalf = y => (y < CN.mast ? 0 : y <= CN.sky ? (y === CN.mast || y === CN.sky ? 1 : 2) : y < CN.pod0 ? 1 : y <= CN.pod1 ? POD_ROWS[y - CN.pod0][0]
  : Math.round(2 + 5.2 * Math.pow((y - CN.pod1) / (WL - CN.pod1), 1.45)));
/** the tower's pixels: shade 0..1 and kind ('shaft', 'pod', 'win', 'sky', 'mast'); null where it isn't */
function cnPixel(x, y) {
  const dx = x - CN.x;
  if (y < CN.top || y >= WL) return null;
  if (y < CN.mast) {                                                                      // the antenna: a steel mast, its foot wider
    if (dx === 0) return { s: 0.55 + (y > 36 ? 0.1 : 0), kind: 'mast' };
    if (dx === -1 && y > 36) return { s: 0.7, kind: 'mast' };
    return null;
  }
  const hw = cnHalf(y);
  if (Math.abs(dx) > hw) return null;
  const u = hw ? dx / hw : 0;                                                             // -1 left edge .. 1 right edge
  if (y <= CN.sky) {                                                                      // the SkyPod
    if (y === CN.mast || y === CN.sky) return { s: 0.48 - u * 0.2, kind: 'sky' };
    if (y === CN.mast + 2 && Math.abs(dx) < 2) return { s: 0.3, kind: 'win' };            // its windows
    return { s: 0.66 - u * 0.3, kind: 'sky' };
  }
  if (y < CN.pod0) return { s: 0.74 - u * 0.34, kind: 'shaft' };                          // the upper shaft
  if (y <= CN.pod1) {
    const kind = POD_ROWS[y - CN.pod0][1];
    if (kind === 'win' || kind === 'win2') return { s: 0.3 - u * 0.1, kind: 'win', edge: Math.abs(dx) === hw };
    if (kind === 'cap') return { s: 0.42 - u * 0.15, kind: 'pod' };
    if (kind === 'rim') return { s: 0.9 - u * 0.25, kind: 'pod' };
    return { s: (kind === 'ring' ? 0.86 : 0.66) - u * 0.32 - (kind === 'neck' ? 0.1 : 0), kind: 'pod' };
  }
  const seam = Math.round(hw * 0.3);                                                      // the shaft: left wing, core, right wing
  let s = u < 0 ? 0.86 + u * 0.06 : 0.62 - u * 0.4;
  if (dx === seam || dx === -seam - 1) s -= 0.18;
  if (dx === -hw) s += 0.08;
  s += 0.06 * Math.exp(-(WL - y) / 22);                                                   // the foot's floodlights
  return { s, kind: 'shaft' };
}
function renderCN(ramp) {
  const p = pix(W, WL);
  for (let y = CN.top; y < WL; y++) for (let x = CN.x - 8; x <= CN.x + 8; x++) {
    const q = cnPixel(x, y);
    if (!q) continue;
    let col;
    if (q.kind === 'win') col = q.edge ? '#5a4a3a' : hash(x, y, 31) < 0.7 ? (y & 1 ? '#ffd890' : '#ffe8b8') : '#7a5a3a';
    else if (q.kind === 'mast') col = pick(['#4a4e5c', '#6a7080', '#9096a6'], q.s, x, y);
    else col = pick(ramp, q.s, x, y, 0.5);
    p.set(x, y, col);
  }
  return trim(p);
}
// the colours of the shows: a ramp each, dark to light, for the shaft, the SkyPod and the white ring
const SHOW_COLS = {
  red: ['#3a0a10', '#6a1018', '#a0161e', '#d42028', '#f0464a', '#ff7a76', '#ffb0a8'],
  white: CN_PLAIN.map(c => mixHex(c, '#fff4f0', 0.25)),
  orange: ['#3a1a08', '#6a300c', '#a04c12', '#d86e1c', '#f6923a', '#ffb86e', '#ffd8a8'],
  yellow: ['#3a3008', '#6a560e', '#a08418', '#d4b426', '#f2d44c', '#fce47e', '#fff2b8'],
  green: ['#0a2a14', '#10461e', '#1a6a2c', '#26963c', '#3ec05a', '#78dc8a', '#b4f0bc'],
  cyan: ['#0a2a34', '#0e4656', '#146a80', '#1e94ac', '#3cbcd0', '#7cdae6', '#b8eef4'],
  blue: ['#0e1440', '#162068', '#203298', '#2e48c8', '#4c6ae8', '#8098f6', '#b8c6fc'],
  violet: ['#200e3a', '#36165e', '#521e8a', '#7028b4', '#9248d8', '#b480ee', '#d8b8fa'],
};
const RAINBOW = ['red', 'orange', 'yellow', 'green', 'cyan', 'blue', 'violet'];

/* ================= The Rogers Centre: the dome's segmented roof over its drum, at the tower's foot ================= */
const DOME_R = ['#2e3442', '#444c5e', '#5e687a', '#7a8496', '#98a2b2', '#b8c0cc'];
function paintDome(p) {
  const { cx, x0, x1, top } = DOME, half = (x1 - x0) / 2, eave = 157;
  for (let x = x0; x <= x1; x++) {
    const u = (x - cx) / half, yt = Math.round(eave - (eave - top) * Math.sqrt(Math.max(0, 1 - u * u)));
    for (let y = yt; y < WL; y++) {
      let col;
      if (y < eave) {                                                                       // the roof: panels, their seams curving down
        const seam = [-0.62, -0.18, 0.28, 0.7].some(s => Math.abs(u - s * (0.55 + 0.45 * (y - top) / (eave - top))) < 0.03);
        let v = 0.62 - u * 0.28 - (y - yt) * 0.025 + (y === yt ? 0.2 : 0);
        if (seam) v -= 0.28;
        col = pick(DOME_R, v, x, y, 0.6);
      } else if (y === eave) col = '#c8ccd6';                                              // the roof's rim, lit
      else col = pick(PRECAST, 0.35 + ((x - x0) % 4 === 0 ? 0.25 : 0) - u * 0.1, x, y);   // the drum's ribs
      p.set(x, y, mixHex(col, hazeAt(x, y), 0.12));
    }
  }
}
const DOME_LIGHTS = [];                             // the lamps round its rim (per frame)
for (let x = DOME.x0 + 3; x < DOME.x1 - 2; x += 5) DOME_LIGHTS.push(x);

/* ================= SkyTower at Pinnacle One Yonge, the gold Royal Bank Plaza, Queens Quay Terminal, the Westin ================= */
function paintSkyTower(p, r) {
  const { x0, x1, top } = SKYT;
  for (let y = top; y < WL; y++) {
    const inset = y < top + 3 ? 2 : y < top + 7 ? 1 : 0;                                    // the sculpted crown steps in
    for (let x = x0 + inset; x < x1 - inset; x++) {
      const u = (x - x0) / (x1 - x0 - 1);
      let v = 0.42 - u * 0.25 + ((x - x0) % 3 === 1 ? 0.12 : 0) + 0.1 * smooth(WL - 50, WL, y);   // vertical fins
      if (x === x0 + inset) v += 0.2;
      p.set(x, y, mixHex(pick(GLASS_B, v, x, y, 0.5), hazeAt(x, y), 0.12));
      // lit windows: the first residents live up to the 56th floor (the lower half), the upper floors are still dark
      if (y > top + 8 && (y & 1) && x > x0 + inset && x < x1 - inset - 1) {
        const share = y > top + 52 ? 0.34 : 0.05, v2 = r();
        if (v2 < share) p.set(x, y, WARMW[Math.floor(r() * 4)]);
        else if (v2 < share + 0.03) addTog(r, x, y, 1, 1, WARMW[0]);
      }
    }
  }
  for (let y = top; y < top + 9; y++) { p.set(x0 + (y < top + 3 ? 2 : y < top + 7 ? 1 : 0), y, '#d8e4f4'); }   // the crown's lit edge
  for (let x = x0 + 2; x < x1 - 2; x++) p.set(x, top, '#e8f0fa');
}
function paintRBP(p) {
  const x0 = 344, x1 = 357, top = 122;                                                      // the south tower, its sawtooth faces of gold glass
  for (let y = top; y < WL; y++) for (let x = x0; x < x1; x++) {
    let v = 0.55 + ((x - x0) % 2 ? -0.18 : 0.08) + ((y - top) % 2 ? -0.06 : 0.04) - (x - x0) * 0.018;
    if (y === top) v = 0.95;
    if (x === x0) v += 0.15;
    if (hash(x, y >> 1, 77) < 0.12) v += 0.22;                                               // offices still lit behind the gold
    p.set(x, y, pick(GOLDR, v, x, y, 0.4));
  }
}
function paintQQT(p, r) {
  const x0 = 238, x1 = 262, roof = 148;                                                     // the old terminal warehouse, its glass rooftop, its tower
  for (let y = roof; y < WL; y++) for (let x = x0; x < x1; x++) {
    let col = pick(PRECAST, 0.5 + (x === x0 ? 0.2 : 0) + ((y - roof) % 3 === 0 ? 0.12 : 0), x, y);
    if ((y - roof) % 3 === 2 && x > x0 && x < x1 - 1 && r() < 0.55) col = WARMW[Math.floor(r() * 4)];
    p.set(x, y, col);
  }
  for (let y = roof - 4; y < roof; y++) for (let x = x0 + 2; x < x1 - 6; x++) p.set(x, y, y === roof - 4 ? '#6a8a98' : y === roof - 2 && x % 3 === 0 && r() < 0.6 ? '#f0dcb0' : '#1e3440');   // the glass storeys on the roof
  for (let y = roof - 9; y < roof; y++) for (let x = x1 - 5; x < x1 - 1; x++) p.set(x, y, y === roof - 9 ? '#c8c0b0' : x === x1 - 5 ? '#8a8078' : '#5a524c');
  p.set(x1 - 4, roof - 6, '#fff4dc'); p.set(x1 - 3, roof - 6, '#f0e0c0');                   // its lit clock
}
function paintWestin(p, r) {
  for (const [x0, x1, top] of [[360, 369, 131], [371, 381, 128]]) for (let y = top; y < WL; y++) for (let x = x0; x < x1; x++) {
    let col = pick(PRECAST, 0.5 + (x === x0 ? 0.25 : x === x1 - 1 ? -0.2 : 0) + (y === top ? 0.3 : 0), x, y);
    if (y > top + 1 && (y & 1) && x > x0 && x < x1 - 1) { const v = r(); if (v < 0.42) col = WARMW[Math.floor(r() * 4)]; else if (v < 0.46) addTog(r, x, y, 1, 1, WARMW[1]); }
    p.set(x, y, col);
  }
}
/* tower cranes on One Yonge's next tower and in the East Bayfront: lattice masts and jibs, a lamp at each tip */
const CRANES = [{ x: 424, top: 98, jib: 84, l: 6, r: 22, trolley: [0.25, 0.9], k: 4 }, { x: 466, top: 124, jib: 112, l: 5, r: 14, trolley: [0.2, 0.85], k: 3 }];
function paintCranes(p) {
  for (let y = 98; y < WL; y++) for (let x = 420; x < 429; x++) {                           // the next tower, half built: a concrete core
    const core = y < 104 ? x > 422 && x < 427 : true;
    if (core) p.set(x, y, mixHex(pick(PRECAST, 0.35 + (x === 420 ? 0.2 : 0) + ((y & 3) === 0 ? 0.1 : 0), x, y), hazeAt(x, y), 0.15));
  }
  for (const c of CRANES) {
    for (let y = c.jib; y < c.top; y++) { p.set(c.x, y, '#c8a040'); if ((y & 1) === 0) p.set(c.x + 1, y, '#7a6430'); }
    for (let x = c.x - c.l; x <= c.x + c.r; x++) { p.set(x, c.jib, '#d8b048'); if ((x & 1) === 0 && x > c.x) p.set(x, c.jib + 1, '#8a7034'); }
    p.set(c.x, c.jib - 1, '#a88838'); p.set(c.x, c.jib - 2, '#a88838');                    // the apex, its ties
    p.line(c.x, c.jib - 2, c.x + c.r, c.jib, '#6a5830'); p.line(c.x, c.jib - 2, c.x - c.l, c.jib, '#6a5830');
    for (let x = c.x - c.l; x < c.x - c.l + 3; x++) { p.set(x, c.jib + 1, '#3a3a44'); p.set(x, c.jib + 2, '#3a3a44'); }   // the counterweight
    p.set(c.x + 1, c.jib + 2, '#e8e0c0');                                                   // the cab's window
  }
}

/* ================= The far shore: Harbourfront's trees, the seawall, marina masts, the ferry terminal ================= */
const QQ_LAMPS = [], MASTS = [];
function paintShore(p, r) {
  for (let x = 0; x < W; x++) {
    p.set(x, WL - 2, pick(['#2a2c3c', '#3a3c4e', '#4c4e60'], 0.5 + 0.3 * Math.sin(x * 0.7), x, WL - 2));
    p.set(x, WL - 1, '#151724');
    if (x % 13 === 5) QQ_LAMPS.push(x);
  }
  // the promenade's trees in front of the buildings, in their autumn colours, dark at this distance
  for (let x = 0; x < W; x += 5 + Math.floor(r() * 6)) {
    if (x > 236 && x < 264) continue;                                                      // the terminal's forecourt
    const rad = 2 + Math.floor(r() * 3), cy = WL - 3 - rad, tint = r();
    for (let y = cy - rad; y <= WL - 3; y++) for (let i = x - rad - 1; i <= x + rad + 1; i++) {
      const d = Math.hypot((i - x) / (rad + 1), (y - cy) / rad);
      if (d > 1 + (bayer(i, y) - 0.5) * 0.4) continue;
      const lit = y > cy + rad * 0.3;
      p.set(i, y, lit ? (tint < 0.4 ? '#4a2a1c' : tint < 0.7 ? '#3e3418' : '#2a2c1c') : (tint < 0.4 ? '#2a1814' : '#1c1c16'));
    }
  }
  for (const x of [188, 193, 197, 202, 207, 290, 295]) {                                    // masts at the marinas, a lamp at each top
    const h = 12 + Math.floor(r() * 8);
    for (let y = WL - h; y < WL - 2; y++) p.set(x, y, '#5a5c6c');
    MASTS.push([x, WL - h - 1]);
  }
  for (let x = 340; x < 357; x++) for (let y = WL - 6; y < WL - 2; y++) p.set(x, y, y === WL - 6 ? '#8a8c9a' : (x % 3 === 0 ? '#e8d4a0' : '#3a3c48'));   // the ferry terminal's shed
}

/* ================= The island's snack kiosk by the ferry dock, and its sign (the city's billboard) ================= */
// A white clapboard kiosk under a green tin roof, its hatch lit and open late; on its roof the sign for Rosie's butter
// tarts, lit by two gooseneck lamps. The sign, its frame, its posts and its lamps are the billboard; the kiosk stays.
const SIDING = ['#2a2a34', '#3e3e4a', '#56565f', '#72727a', '#9a9aa0'];
const TIN = ['#14261c', '#1e3626', '#2a4a32', '#3a6040', '#507a54'];
const HATCH = { x0: 74, x1: 108, y0: 166, y1: 183 };
function renderKiosk() {
  const { x0, x1, roof, eave, base } = KIOSK, p = pix(W, H);
  for (let y = roof; y <= eave; y++) {                                                       // the hipped tin roof, its ribs
    const inset = Math.round((eave - y) * 0.9);
    for (let x = x0 - 2 + inset; x <= x1 + 2 - inset; x++) {
      let v = 0.35 + (y - roof) * 0.04 + ((x - x0) % 4 === 0 ? 0.16 : 0) - (x - x0) * 0.002;
      if (y === roof) v = 0.7;
      if (y === eave) v = 0.15;
      p.set(x, y, pick(TIN, v, x, y, 0.6));
    }
  }
  for (let y = eave + 1; y < base; y++) for (let x = x0; x <= x1; x++) {                     // the clapboards, lit from the hatch
    const hl = Math.exp(-(((x - KIOSK.cx) / 30) ** 2) - (((y - 186) / 18) ** 2));
    let v = 0.3 + 0.45 * hl + ((y - eave) % 3 === 0 ? -0.18 : 0) - (y > base - 4 ? 0.2 : 0);
    if (x === x0) v -= 0.15; else if (x === x1) v -= 0.25;
    p.set(x, y, pick(SIDING, v, x, y, 0.6));
  }
  const { x0: hx0, x1: hx1, y0: hy0, y1: hy1 } = HATCH;
  for (let y = hy0; y <= hy1; y++) for (let x = hx0; x <= hx1; x++) {                        // the hatch: the lit inside, shelves, the urn
    let col = pick(['#c8803a', '#e8a050', '#f8c878', '#ffe2a8'], 0.3 + 0.7 * smooth(hy1, hy0, y) - Math.abs(x - KIOSK.cx) * 0.008, x, y);
    if (y === hy0 + 4 || y === hy0 + 8) col = '#8a5a30';                                     // shelves
    if (y === hy0 + 3 && (x % 3 === 0)) col = ['#c83a2a', '#3a7a4a', '#e8d070'][x % 9 / 3 | 0];   // cups and tins on the shelf
    if (y === hy0 + 7 && x > hx0 + 4 && x < hx0 + 12) col = x % 2 ? '#f4e0b0' : '#d8a050';              // a tray of tarts
    if (x >= hx1 - 8 && x <= hx1 - 5 && y >= hy0 + 9 && y <= hy1 - 2) col = x === hx1 - 8 ? '#d8dce4' : '#9aa0aa';   // the urn
    p.set(x, y, col);
  }
  for (let x = hx0 - 2; x <= hx1 + 2; x++) {                                                 // the awning over it (red and white) and the counter
    for (let y = hy0 - 4; y < hy0; y++) p.set(x, y, ((x - hx0 + 40) >> 1) % 2 ? '#e8e0d0' : '#c02a2a');
    p.set(x, hy0 - 4, '#f4ece0');
    p.set(x, hy1 + 1, '#a8a0a0'); p.set(x, hy1 + 2, '#5a5458');
  }
  for (let y = hy1 + 3; y < base - 2; y++) p.set(hx0 + 4, y, '#2a2a30');                     // the door's edge, a menu board
  for (let y = 190; y < 204; y++) for (let x = 94; x < 108; x++) p.set(x, y, y === 190 || y === 203 || x === 94 || x === 107 ? '#4a3a2c' : '#1e2a22');
  for (const [x, y] of [[96, 193], [97, 193], [99, 193], [100, 193], [101, 193], [96, 196], [97, 196], [98, 196], [100, 196], [96, 199], [98, 199], [99, 199], [100, 199], [102, 199]]) p.set(x, y, '#c8d0c0');   // chalk lines
  return trim(p);
}
// the sign: the frame and posts (BOARD_C), the ad itself (AD_C), its two lamps drawn per frame
function renderBoard() {
  const p = pix(W, H), L = BB.x - 2, Rr = BB.x + BB.w + 1, T0 = BB.y - 2, B = BB.y + BB.h + 1;
  for (const lx of [BB.x + 8, BB.x + BB.w - 9]) for (let y = B; y <= KIOSK.roof + 1; y++) { p.set(lx, y, '#1a1c24'); p.set(lx + 1, y, '#0e1016'); }
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) {
    let col = '#22242e';
    if (y === T0 || x === L) col = '#4a4c58'; else if (y === B || x === Rr) col = '#121318';
    p.set(x, y, col);
  }
  for (const lx of [BB.x + 10, BB.x + BB.w - 11]) {                                           // gooseneck lamp arms over the top
    p.line(lx, T0, lx, T0 - 4, '#2a2c36'); p.line(lx, T0 - 4, lx + 3, T0 - 5, '#2a2c36');
    p.set(lx + 3, T0 - 4, '#3a3c48'); p.set(lx + 4, T0 - 4, '#3a3c48');
  }
  return trim(p);
}
const TART = [
  '....oooooo....', '..ooffffffoo..', '.offfffhhffffo', 'offffffhfffffo', 'offfrfffffrffo', '.offffffffffo.',
  'cCcCcCcCcCcCcC', '.ccccccccccccc', '..cccccccccc..', '...dddddddd...'];
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, pick(['#c8b48c', '#e0cca0', '#f0dcb0'], 0.85 - j * 0.02 + (i < 17 ? -0.1 : 0), i, j));
  for (let i = 0; i < w; i++) { p.set(i, 0, '#8a2020'); p.set(i, h - 1, '#8a2020'); }
  const key = { o: '#8a4a1a', f: '#6a2c0c', h: '#d89048', r: '#2a120a', c: '#e0a848', C: '#c88632', d: '#8a5a24' };
  TART.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) p.set(2 + i, 5 + j, key[row[i]]); });
  p.set(6, 3, '#f4ece0'); p.set(9, 2, '#f4ece0'); p.set(11, 3, '#f4ece0');                    // steam, it's warm
  const ink = (s, x, y, col) => { let xx = x; for (const ch of s) { const g = FONTS[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.set(xx + i, y + j, col); }); xx += 4; } };
  ink("ROSIE'S", 18, 2, ACCENT);
  ink('BUTTER', 18, 8, '#3a1c0e');
  ink('TARTS', 18, 14, '#3a1c0e');
  return gradeAd(p.done(), { level: 0.86, tint: '#141a30', mix: 0.1, wear: 0.3, seed: 7 });
}
// the letters the sign spells (the 3x5 font's, written out: the ad is baked once)
const FONTS = {
  R: ['110', '101', '110', '101', '101'], O: ['010', '101', '101', '101', '010'], S: ['011', '100', '010', '001', '110'],
  I: ['111', '010', '010', '010', '111'], E: ['111', '100', '110', '100', '111'], "'": ['010', '010', '000', '000', '000'],
  B: ['110', '101', '110', '101', '110'], U: ['101', '101', '101', '101', '111'], T: ['111', '010', '010', '010', '010'],
  A: ['010', '101', '111', '101', '101'],
};

/* ================= The harbour: dark water, the waterfront's mirror broken by ripples, the lights' long streaks ================= */
function renderWater() {
  const h = QY - WL, p = pix(W, h), P = ['#03050f', '#05081a', '#080c21', '#0b1028', '#0f152f'];
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++)
    p.set(x, y, pick(P, 0.66 - 0.5 * (y / h) + (vnoise(x / 40, y / 3, 9) - 0.5) * 0.18, x, y, 0.5));
  return p.done();
}
/** the far side as the water mirrors it: a still of what stands over the waterline, upside down, softened and dimmed */
function renderMirror(src) {
  const sx = src.getContext('2d'), data = sx.getImageData(0, 0, W, WL).data, rows = QY - WL, p = pix(W, rows);
  for (let k = 0; k < rows; k++) for (let i = 0; i < W; i++) {
    const sy = WL - 1 - k * 1.6, acc = [0, 0, 0];
    let n = 0;
    for (let dy = -2; dy <= 2; dy++) for (let dx = -1; dx <= 1; dx++) {
      const yy = Math.round(sy + dy), xx = clamp(i + dx, 0, W - 1);
      if (yy < 0 || yy >= WL) continue;
      const o = (yy * W + xx) * 4;
      acc[0] += data[o]; acc[1] += data[o + 1]; acc[2] += data[o + 2]; n++;
    }
    const f = 0.62 - k * 0.008;
    p.set(i, k, [0, 1, 2].map(c => Math.min(255, Math.floor((acc[c] / n * f + [3, 5, 16][c] * (1 - f)) / 12 + bayer(i, k)) * 12)));
  }
  return p.done();
}
const GLINTS = [];                                  // ripples catching the light, drifting
{
  const r = rng(909);
  for (let i = 0; i < 120; i++) GLINTS.push({ y: WL + 2 + Math.floor(r() * (QY - WL - 3)), x: r(), k: (r() < 0.5 ? -1 : 1) * (1 + Math.floor(r() * 4)),
    len: 2 + Math.floor(r() * 6), kf: 18 + Math.floor(r() * 40), ph: r(), c: r() < 0.25 ? '#c8a070' : r() < 0.7 ? '#4a5478' : '#010309' });
}
// the brightest lights lay long, broken streaks across the water (the CN Tower's follows its colour: drawn per frame)
const STREAKS = [
  { x: 350, w: 10, col: '#e8b048', a: 0.24, seed: 2 }, { x: 410, w: 4, col: '#d8e0f0', a: 0.12, seed: 3 }, { x: 375, w: 10, col: '#ffd090', a: 0.18, seed: 4 },
  { x: 250, w: 10, col: '#ffd090', a: 0.18, seed: 5 }, { x: 281, w: 4, col: '#e0e8f4', a: 0.1, seed: 6 }, { x: 330, w: 7, col: '#d8e4f8', a: 0.14, seed: 7 },
  { x: 140, w: 12, col: '#b8c0d0', a: 0.1, seed: 8 }, { x: 100, w: 8, col: '#ffd8a0', a: 0.1, seed: 9 }, { x: 30, w: 8, col: '#ffd8a0', a: 0.1, seed: 10 },
  { x: 214, w: 6, col: '#ffe0b0', a: 0.12, seed: 11 }, { x: 452, w: 8, col: '#ffd8a0', a: 0.12, seed: 12 },
];

/* ================= Boats ================= */
// an island ferry, double-ended, two decks: white over a dark green hull band, the lower saloon lit, the open upper deck
// with its rail and string of bulbs, the wheelhouse on top, the flag at the stern. Bow to the left (it sails west).
const FRY = { w: 44, h: 17 };
function renderFerry() {
  const p = pix(FRY.w, FRY.h), wl = FRY.h - 1;
  for (let y = 12; y <= wl; y++) for (let x = 0; x < FRY.w; x++) {
    const rake = Math.max(0, y - 13);
    if (x < rake || x > FRY.w - 1 - rake) continue;
    p.set(x, y, y === 12 ? '#f2f2ec' : y <= 14 ? (x % 6 === 2 ? '#2a5a40' : '#1e4a34') : y === 15 ? '#141c1a' : '#0a0e10');
  }
  for (let x = 2; x < FRY.w - 2; x++) {                                                      // the lower saloon
    p.set(x, 7, '#e8e8e2');
    for (let y = 8; y <= 11; y++) p.set(x, y, x % 4 === 0 ? '#d8d8d2' : y === 11 ? '#c08040' : y === 8 ? '#ffe4b0' : '#f8c878');
  }
  for (let x = 3; x < FRY.w - 3; x++) { p.set(x, 6, '#d8d8d0'); if (x % 2 === 0) p.set(x, 5, '#b8b8b0'); p.set(x, 4, '#e0e0d8'); }   // the upper deck's rail
  for (let x = 17; x <= 26; x++) for (let y = 0; y <= 3; y++) p.set(x, y, y === 0 ? '#f4f4ee' : y === 2 && x > 17 && x < 26 ? (x % 2 ? '#ffe8b8' : '#8ac0e0') : '#dcdcd4');   // the wheelhouse
  for (let y = 0; y <= 6; y++) p.set(FRY.w - 4, y, '#8a8a90');                               // the flagstaff at the stern
  p.set(FRY.w - 3, 0, '#e02020'); p.set(FRY.w - 2, 0, '#f4f0f0'); p.set(FRY.w - 1, 0, '#e02020');
  p.set(FRY.w - 3, 1, '#c01818'); p.set(FRY.w - 2, 1, '#e0dcdc'); p.set(FRY.w - 1, 1, '#c01818');
  return p.done();
}
const FERRY_BULBS = [];
for (let x = 4; x < FRY.w - 4; x += 3) FERRY_BULBS.push(x);
const FERRY_FOLK = [];                              // passengers at the upper rail: x in the sprite, coat, hair
{
  const r = rng(808);
  for (let x = 4; x < FRY.w - 5; x += 2 + Math.floor(r() * 2)) if (x < 16 || x > 27) FERRY_FOLK.push({ x, coat: ['#c83a3a', '#2a4a7a', '#e8d070', '#3a6a4a', '#8a8e9e', '#e0e0d8'][Math.floor(r() * 6)], skin: r() < 0.5 ? '#e0b088' : '#a87048', wave: r() < 0.45 });
}
// a yellow water taxi, bow to the right: the cabin lit, a white masthead light
const TAXI_ROWS = ['.......w......', '....wwwwwww...', '....gyygyyg...', '....wwwwwwwwk.', 'yyyyyyyyyyyyyy', '.yyyyyyyyyyyy.', '..dddddddddd..'];
const TAXI_KEY = { w: '#e8e8e4', g: '#8ac0e0', y: '#e8b830', k: '#3a3a44', d: '#5a4410' };
TAXI_KEY.y = '#e8b830';
// a turboprop on final approach, nose to the left, gear down: white fuselage, a dark blue tail, the high wing and engine
const PLANE_ROWS = [
  '................bbbbbb', '..................bbb.', '......gggggggg....bbb.', '.wwwwnnnwwwwwwwwwwbbbb', 'wwwwwnnnwwwwwwwwwwwwww', '.ssssssssssssssssssss.',
  '...k....k.............', '...k....k.............'];
const PLANE_KEY = { b: '#1e2e5a', g: '#5a6070', w: '#c8ccd6', n: '#8a8e9a', s: '#7a7e8a', k: '#2a2c34' };
const PLANE_WINDOWS = [10, 12, 14, 16, 18];          // the cabin's lit windows, along row 4
/* Canada geese: black head and neck, the white chinstrap, a brown back, pale breast, black tail. On the grass (walking,
   head down grazing, head up on watch), on the water (swimming), and in flight (wings up, level, down) */
const GOOSE_KEY = { k: '#16161a', w: '#f0ece4', b: '#7e6a50', B: '#54463a', p: '#cfc2ae', t: '#1e1e22', y: '#3a3430' };
const GOOSE_WALK = [
  ['...kk...........', '..kkk...........', '..kwk...........', '...wk...........', '...k............', '...k.......BB...', '...kk...bbbbBBB.', '....pbbbbbbbbBBt',
    '....ppbbbbbbBBtt', '.....pppppbbBt..', '.......pppp.....', '.......y..y.....', '......yy.yy.....'],
  ['...kk...........', '..kkk...........', '..kwk...........', '...wk...........', '...k............', '...k.......BB...', '...kk...bbbbBBB.', '....pbbbbbbbbBBt',
    '....ppbbbbbbBBtt', '.....pppppbbBt..', '.......pppp.....', '........yy......', '.......yyy......']];
const GOOSE_GRAZE = ['................', '................', '................', '................', '................', '...........BB...', '........bbbbBBB.',
  '....bbbbbbbbbBBt', '...kpbbbbbbbBBtt', '..kk.pppppbbBt..', '.kk....pppp.....', 'kwk....y..y.....', 'kk....yy.yy.....'];
const GOOSE_SWIM = ['..kk......', '..kw......', '..k.......', '..k..bbB..', '..kbbbbBBt', '...pppppp.'];
const GOOSE_FLY = [['.b....b..', '..bb.bb..', 'kk.bbb.Bt', '...ppp...'], ['.........', 'kkbbbbbBt', '..bpppb..', '.........'], ['.........', 'kk.bbbbBt', '..bpppb..', '.b.....b.']];

/* ================= The island's side: the seawall's edge, the path, the lawn under fallen leaves ================= */
const PAVE = ['#14161f', '#1c1f2a', '#262a36', '#323644', '#3e4252'];
const LAWN = ['#0a120c', '#101a12', '#162418', '#1e2e1e', '#283a26'];
const WARMG = ['#3a3430', '#5a4c3e', '#806a4e', '#a88a5e', '#d0ae76', '#ecd09a'];
const LEAFC = ['#8a1a12', '#b82a18', '#d8461e', '#e8702a', '#f09a38', '#d8b03a', '#9a3a1a'];
function groundLight(x, y) {
  let s = 0;
  for (const L of LAMPS) s += 0.62 * Math.exp(-(((x - L.x) / 38) ** 2) - (((y - 214) / 14) ** 2));
  s += 0.5 * Math.exp(-(((x - KIOSK.cx) / 26) ** 2) - (((y - 218) / 10) ** 2));                   // the kiosk's hatch
  return Math.min(1, s);
}
function renderGround() {
  const Y0 = QY - 1, p = pix(W, H - Y0), put = (x, y, c) => p.set(x, y - Y0, c), r = rng(4242);
  for (let x = 0; x < W; x++) {
    put(x, QY - 1, hash(x >> 1, 3, 21) < 0.5 ? '#6a6c7a' : '#585a68');                        // the cap's far edge, catching the skyline's light
    for (let y = QY; y <= QY + 2; y++) {
      const g = groundLight(x, y), joint = x % 19 === 0;
      put(x, y, joint ? '#14151c' : g > 0.25 ? pick(WARMG, 0.3 + g * 0.55 - (y - QY) * 0.08, x, y) : pick(['#2a2c38', '#3a3c4a', '#4a4c5a'], 0.7 - (y - QY) * 0.25, x, y));
    }
    put(x, QY + 3, '#0c0d14');                                                               // the cap's shadow
    for (let y = QY + 4; y < H; y++) {
      const g = groundLight(x, y), edgeG = GRASS + Math.round(2 * vnoise(x / 7, 1, 27) - 1);
      let col;
      if (y < edgeG) {                                                                       // the path
        const v = 0.42 + (vnoise(x / 14, y / 3, 23) - 0.5) * 0.18 + (hash(x, y, 24) - 0.5) * 0.08 + (y - QY) * 0.004;
        col = g + (bayer(x, y) - 0.5) * 0.3 > 0.36 ? pick(WARMG, 0.08 + g * 0.6 + (v - 0.4) * 0.5, x, y, 0.6) : pick(PAVE, v + g * 0.3, x, y, 0.6);
      } else {                                                                               // the lawn
        const v = 0.38 + (vnoise(x / 6, y / 2, 25) - 0.5) * 0.3 + (y - GRASS) * 0.006 + (y === edgeG ? -0.2 : 0);
        col = pick(LAWN, v + g * 0.5, x, y, 0.8);
        if (g > 0.35 && hash(x, y, 26) < g * 0.3) col = '#3a4a2a';
      }
      put(x, y, col);
    }
  }
  // fallen leaves: thick under the maple, gold under the willow, a scatter blown along the path
  for (let k = 0; k < 2600; k++) {
    const x = Math.floor(r() * W), y = QY + 4 + Math.floor(r() * (H - QY - 4));
    const near = Math.exp(-(((x - 40) / 70) ** 2)) * 0.9 + Math.exp(-(((x - 450) / 50) ** 2)) * 0.7 + (y > GRASS ? 0.25 : 0.08);
    if (r() > near) continue;
    const g = groundLight(x, y), willow = x > 400 && r() < 0.6, big = y > 240 ? 2 : 1;
    let col = willow ? (r() < 0.6 ? '#c8a838' : '#a88a2a') : LEAFC[Math.floor(r() * LEAFC.length)];
    col = mixHex(col, '#0a0c18', 0.55 - g * 0.4 - (y - QY) * 0.003);
    put(x, y, col); if (big > 1) put(x + 1, y, col);
    if (r() < 0.3) put(x, y - 1, mixHex(col, '#000000', 0.3));
  }
  return { c: p.done(), x: 0, y: Y0 };
}
/* a lamp post of the island's: a black pole, an acorn lantern on top */
function renderLamp() {
  const p = pix(9, 70), K = '#0e1016', KH = '#2e3240', cx = 4;
  for (let y = 12; y < 68; y++) { p.set(cx, y, K); p.set(cx - 1, y, y < 40 ? KH : '#1c1e28'); }
  for (let y = 62; y < 70; y++) { const hw = y < 65 ? 1 : 2; for (let x = cx - hw; x <= cx + hw; x++) p.set(x, y, x === cx - hw ? KH : K); }
  for (let x = cx - 2; x <= cx + 2; x++) { p.set(x, 11, K); p.set(x, 12, KH); }
  [1, 2, 3, 3, 3, 3, 2, 2].forEach((hw, j) => { for (let x = cx - hw; x <= cx + hw; x++) p.set(x, 2 + j, j < 3 ? '#fffbea' : j < 6 ? '#fff0c8' : '#f0d090'); });   // the acorn globe
  p.set(cx, 0, K); p.set(cx, 1, K); p.set(cx, 10, K);
  return p.done();
}
/* a park bench, seen from behind: slats of the back, the seat, cast-iron ends */
function renderBench() {
  const p = pix(30, 14), K = '#101218', WOOD = '#3e2c20', WOODL = '#5e4430', WOODH = '#7a5a3e';
  for (let x = 1; x < 29; x++) { p.set(x, 0, WOODH); p.set(x, 1, WOOD); p.set(x, 3, WOODL); p.set(x, 4, WOOD); p.set(x, 7, WOODL); }
  for (const lx of [2, 27]) for (let y = 0; y < 14; y++) { p.set(lx, y, K); if (y > 8) p.set(lx + (lx < 10 ? -1 : 1), y, '#22242e'); }
  return p.done();
}
/* a park bin, dark green, its lid on a hinge (lid: 0 shut .. 1 pushed up) */
function renderBin(lid) {
  const p = pix(10, 14), G = ['#0c1a12', '#14281a', '#1e3a24', '#2c4e30'];
  for (let y = 3; y < 14; y++) for (let x = 1; x < 9; x++) p.set(x, y, pick(G, 0.55 - (x - 1) * 0.06 + (y === 3 ? 0.3 : 0) + ((x - 1) % 3 === 0 ? -0.15 : 0), x, y));
  if (lid) { p.line(1, 2, 7, 0, '#3a5a3c'); p.line(2, 2, 8, 0, '#2c4e30'); } else for (let x = 0; x < 10; x++) { p.set(x, 2, '#3a5a3c'); if (x > 2 && x < 7) p.set(x, 1, '#2c4e30'); }
  return p.done();
}
/* the red maple: its trunk and limbs, a full crown of scarlet and orange leaves over the top-left, lit from below */
const MAPLE_R = ['#1a0707', '#300b09', '#4e120e', '#741c16', '#9c2a1a', '#c23e1e', '#e0622a', '#f4923e'];
const MAPLE_TIPS = [];                              // leaves in the crown that stir in the breeze (per frame)
function renderMaple() {
  const p = pix(130, 260), { x: tx, base } = MAPLE;
  for (let y = 118; y <= base; y++) {                                                          // the trunk, its lamp-lit right side
    const u = (base - y) / (base - 118), w = Math.round(lerp(7, 4, u)), x0 = Math.round(tx - (w >> 1) + 3 * u * u + Math.sin(y * 0.06));
    for (let x = x0; x < x0 + w; x++) p.set(x, y, x === x0 + w - 1 ? '#5a3a2a' : x === x0 + w - 2 ? '#2e1e16' : hash(x, y >> 1, 40) < 0.3 ? '#1c1410' : '#120c0a');
  }
  for (let y = base - 3; y <= base; y++) for (let x = tx - 6; x <= tx + 7; x++) if (Math.abs(x - tx - 1) < 4 + (y - base + 3) * 1.4) p.set(x, y, '#100a08');   // the roots' flare
  const limbs = [[tx + 3, 122, tx - 16, 62, 3], [tx + 3, 122, tx + 30, 52, 3], [tx + 14, 92, tx + 62, 70, 2], [tx + 4, 104, tx + 8, 30, 2], [tx + 30, 54, tx + 70, 34, 2]];
  const ends = [];
  for (const [x0, y0, x1, y1, wdt] of limbs) {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let k = 0; k <= n; k++) {
      const u = k / n, x = lerp(x0, x1, u) + Math.sin(u * 4 + x0) * 1.5, y = lerp(y0, y1, u), w = Math.max(1, Math.round(wdt * (1 - u * 0.6)));
      for (let i = 0; i < w; i++) p.set(x + i, y, i === w - 1 ? '#3a2618' : '#140e0a');
      if (k % 9 === 4) ends.push([x, y]);
    }
    ends.push([x1, y1]);
  }
  const blobs = [], r = rng(616);                                                              // the crown: clumps of leaves round the limbs
  for (const [x, y] of ends) blobs.push({ x: x + (r() - 0.5) * 10, y: y - 4 - r() * 8, rr: 12 + r() * 10 });
  for (let i = 0; i < 18; i++) blobs.push({ x: -10 + r() * 96, y: -10 + r() * 60, rr: 12 + r() * 12 });
  for (let y = 0; y < 116; y++) for (let x = 0; x < 112; x++) {
    let best = 0, by = 0;
    for (const b of blobs) { const d = 1 - Math.hypot(x - b.x, (y - b.y) * 1.15) / b.rr; if (d > best) { best = d; by = b.y; } }
    const n = vnoise(x / 2.6, y / 2.6, 617), n2 = vnoise(x / 7, y / 6, 621), edge = best + (n - 0.5) * 0.45;
    if (edge < 0.1) continue;
    if (n2 > 0.78 && best < 0.4 && n > 0.55) continue;                                          // a few gaps where the sky shows through
    const under = clamp((y - by) / 12, -1, 1), lamp = Math.exp(-(((x - 100) / 46) ** 2) - (((y - 124) / 34) ** 2));
    const v = 0.4 + under * 0.16 + (n - 0.5) * 0.46 + (n2 - 0.5) * 0.3 + lamp * 0.32 - (y < 24 ? 0.12 : 0) - (edge < 0.2 ? 0.08 : 0);
    p.set(x, y, pick(MAPLE_R, v, x, y, 0.8));
    if (hash(x, y, 618) < 0.14) MAPLE_TIPS.push({ x, y, ph: hash(x, y, 619), k: 24 + Math.floor(hash(x, y, 620) * 40), col: pick(MAPLE_R, v + 0.28, x, y) });
  }
  return { c: p.done(), x: 0, y: 0 };
}
/* the willow: its leaning trunk, the mass of its crown, and the hanging strands (each in three pieces that sway, per frame) */
const STRANDS = [];
{
  const r = rng(717);
  for (let i = 0; i < 64; i++) {
    const x = Math.round(419 + r() * 64), d = Math.hypot((x - 452) / 50, 0), y0 = Math.round(34 + 30 * d + r() * 14);
    STRANDS.push({ x, y0, len: Math.round(36 + r() * 70 * (0.5 + 0.5 * smooth(420, 470, x))), k: 2 + Math.floor(r() * 3), ph: r(), amp: 1.2 + r() * 2.2, seed: i });
  }
}
const WILLOW_COLS = ['#1e1e0c', '#363412', '#56501c', '#7e7428', '#a8983a'];
let STRAND_C = [];
function renderStrand(s) {
  const p = pix(3, s.len);
  for (let j = 0; j < s.len; j++) {
    const v = 0.3 + 0.35 * hash(s.seed, j, 91) + (j / s.len) * 0.2;
    p.set(1, j, pick(WILLOW_COLS, v, s.seed, j));
    if (hash(s.seed, j, 92) < 0.35) p.set(hash(s.seed, j, 93) < 0.5 ? 0 : 2, j, pick(WILLOW_COLS, v + 0.15, s.seed + 1, j));   // a leaf sticking out
  }
  return p.done();
}
function renderWillow() {
  const p = pix(W, H), { x: tx, base } = WILLOW;
  for (let y = 60; y <= base; y++) {
    const u = (base - y) / (base - 60), w = Math.round(lerp(9, 5, u)), x0 = Math.round(tx - (w >> 1) - 7 * u * u + Math.sin(y * 0.05));
    for (let x = x0; x < x0 + w; x++) p.set(x, y, x === x0 ? '#4a3a2c' : x === x0 + 1 ? '#2a2018' : x === x0 + w - 1 ? '#0a0806' : hash(x, y >> 1, 41) < 0.3 ? '#221a12' : '#16110c');
  }
  for (const [x1, y1] of [[424, 34], [472, 18], [440, 6], [486, 44], [458, 40]]) { p.line(tx - 6, 64, x1, y1, '#16120c'); p.line(tx - 5, 64, x1 + 1, y1, '#221a12'); }
  for (let y = 0; y < 84; y++) for (let x = 404; x < W; x++) {                                // the crown's mass behind the strands
    const n = vnoise(x / 3.5, y / 3, 718), n2 = vnoise(x / 9, y / 7, 719), d = Math.hypot((x - 454) / 50, (y - 24) / 50);
    if (d + (n - 0.5) * 0.45 > 1) continue;
    p.set(x, y, pick(WILLOW_COLS, 0.18 + (n - 0.5) * 0.5 + (n2 - 0.5) * 0.4 + y * 0.004, x, y, 0.8));
  }
  return trim(p);
}
/* the raccoon, facing left: walking (two frames) and up on its hind legs against the bin */
const COON_KEY = { g: '#4a4a52', G: '#6a6a72', k: '#121216', w: '#c8c8cc', t: '#2a2a30', T: '#8a8a90', e: '#e8d8a0' };
const COON_WALK = [['.G.G..........', 'GgGg..........', 'wkekgggggg....', '.gGGGGGGGgggtT', '..gggggggg..tT', '..g.g..g.g....'],
  ['.G.G..........', 'GgGg..........', 'wkekgggggg....', '.gGGGGGGGgggtT', '..gggggggg..Tt', '...gg...gg....']];
const COON_UP = ['.G.G..', 'GgGg..', 'wkek..', '.gg...', 'kgGg..', '.gGgg.', '.gGgg.', '.gggg.', '.gggt.', '.g.gT.', '.g.gt.'];

/* ================= People ================= */
// One function draws everyone (after rome.js and stockholm.js): a look gives the proportions for a height and the
// colours dimmed for the night; person() puts it on its feet. October clothes: toques, scarves, puffer and rain jackets.
// Seen from behind, the shoulders and the hair catch the skyline's glow across the water; the lamps warm the rest.
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#c8c4c0'];
const NIGHT = '#0c1026', GLOWC = '#d8d0e8';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.32), bot: m(bot), shoe: m('#1a161c'), long: o.long || 0, coat: o.coat || 0,
    hat: o.hat ? m(o.hat) : null, pom: o.pom ? m(o.pom) : null, scarf: o.scarf ? m(o.scarf) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind (looking at the view), 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (camera, cup, tart), 3 out, 4 up and out (pointing high), 5 round a
 * neighbour's back; o.lean: the head towards a neighbour; o.sit: seated; o.bend: bent forward (profile)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, bend = side && o.bend ? 1 : 0;
  const ty = fy - legs - th + 1 + bend, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1) + bend * dir;
  if (legs) {
    const ly = fy - legs + 1;
    ctx.fillStyle = 'rgba(8,10,24,0.34)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); ctx.fillRect(x - (bw >> 1), fy + 2, bw, 1);
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th + P.coat, P.top);                                                    // the jacket, longer for some
  if (!side) R(tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                            // the skyline's glow on the shoulders
  if (P.scarf) R(tx + 1, ty, bw - 2, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else { R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD); }
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0) + bend * dir * 2;
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k) + bend * dir, ty + 1 - k + 1, 1, 1, P.topD); R(x + dir * 4 + bend * dir, ty - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hr = hh >= 5 ? 2 : 1, hY = hy + bend;
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair); R(hx, hY, hw, 1, P.hairL);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair); else R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) {                                                                         // a toque, a pompom on some
    R(hx, hY - 1, hw, 2 + (view === 0 ? 1 : 0), P.hat); if (!side) R(hx, hY + 1, hw, 1, mixHex(P.hat, NIGHT, 0.3));
    if (P.pom) R(hx + (hw >> 1) - (side ? dir > 0 ? 1 : 0 : 0), hY - 2, 2, 1, P.pom);
  }
  return hY;
}
const tyOf = (P, fy) => fy - P.ll - P.th + 1;
const L_HE = look(23, 1, 0, '#2a3a5e', '#1e2030', { scarf: '#c8323a', coat: 1 });
const L_SHE = look(22, 0, 4, '#b8a07a', '#2a2a3a', { hat: '#e8e2d8', pom: '#c8323a', long: 3, coat: 2 });
const L_PHOTO = look(24, 2, 0, '#3a4a3a', '#1e2030', { hat: '#2a2a34', coat: 1 });
const L_FRA = look(22, 0, 3, '#d86a2a', '#2a2a3a', { long: 2 });
const L_FRB = look(22, 3, 0, '#3a6a8a', '#1e2030', { hat: '#d8c040' });
const L_KID = look(14, 1, 2, '#e8c030', '#2a3a6a', { hat: '#c8323a', pom: '#f4f0e8', dk: 0.22 });
const L_PARENT = look(23, 1, 1, '#6a3a5a', '#262838', { coat: 2 });
const L_FISH = look(24, 2, 5, '#4a5a3a', '#2a2a30', { hat: '#8a6a3a', coat: 1 });
const L_PHONE = look(23, 3, 0, '#8a8e9e', '#1e2030', { dk: 0.26 });
const L_DOGW = look(26, 0, 1, '#a83a2a', '#2a2a38', { hat: '#2a3a5a', pom: '#e8e0d0', dk: 0.32, coat: 2 });
const L_JOG = look(28, 2, 0, '#2f8a6a', '#1a1c28', { hat: '#1a1c24', dk: 0.32 });
const L_STROLL_A = look(26, 1, 2, '#4a5a8a', '#2a2a38', { scarf: '#d8a030', coat: 2, dk: 0.32 }), L_STROLL_B = look(25, 0, 3, '#8a3a4a', '#1e2030', { long: 3, coat: 3, dk: 0.32 });
const L_BIKE = [look(24, 0, 4, '#e8d050', '#2a2a38', { hat: '#3a7a5a', dk: 0.3 }), look(24, 2, 0, '#c84a3a', '#1e2030', { dk: 0.3 }), look(23, 1, 3, '#3a7aa8', '#2a2a38', { long: 2, dk: 0.3 })];
const VENDOR = look(22, 1, 1, '#e8e4dc', '#2a2a30', { hat: '#c02a2a', dk: 0.12 });
const SPOTS = { friends: [128, 137], photo: { x: 168, tri: 158 }, couple: [230, 242], fisher: 300, kid: 352, parent: 362, phone: 392 };
/* a bicycle under its rider, in profile: two wheels, the frame, the bars (x the saddle, fy the ground) */
function drawBike(x, fy, dir, P, t, ring) {
  const wy = fy - 3, fx = x + dir * 5, bx = x - dir * 5, spin = step(t, 960) & 1;
  for (const cx of [fx, bx]) {
    ctx.fillStyle = '#16181f';
    ctx.fillRect(cx - 1, wy - 3, 3, 1); ctx.fillRect(cx - 1, wy + 3, 3, 1); ctx.fillRect(cx - 3, wy - 1, 1, 3); ctx.fillRect(cx + 3, wy - 1, 1, 3);
    ctx.fillRect(cx - 2, wy - 2, 1, 1); ctx.fillRect(cx + 2, wy - 2, 1, 1); ctx.fillRect(cx - 2, wy + 2, 1, 1); ctx.fillRect(cx + 2, wy + 2, 1, 1);
    dot(cx + (spin ? 1 : -1), wy + (spin ? -1 : 1), '#5a5c66');
  }
  ctx.fillStyle = 'rgba(8,10,24,0.34)'; ctx.fillRect(Math.min(fx, bx) - 3, fy + 1, 14, 1);
  ctx.fillStyle = '#8a2a2a';
  for (let k = 0; k <= 5; k++) ctx.fillRect(Math.round(bx + dir * k), Math.round(wy - k * 0.6), 1, 1);   // the frame, saddle to bars
  for (let k = 0; k <= 4; k++) ctx.fillRect(Math.round(x + dir * k), wy - 3, 1, 1);
  R(fx - dir, wy - 5, 1, 2, '#16181f'); R(fx - dir * 2, wy - 5, 2, 1, '#16181f');
  R(x - 1, wy - 4, 3, 1, '#0e0f14');
  dot(fx + dir * 1, wy - 4, '#fff4d0'); halo(HL.tiny, fx + dir * 2, wy - 4, 0.9);                // the headlamp
  dot(bx - dir * 3, wy - 2, '#ff3a30');                                                   // the tail lamp
  const ty = wy - 4 - P.th;
  R(x - 2, ty, P.tw - 2, P.th, P.top); R(x - 2, ty, P.tw - 2, 1, P.topL);                  // the rider, leaning to the bars
  R(x + dir * 2, ty + 2, 1, 1, P.topD); R(x + dir * 3, ty + 3, 1, 1, P.topD); R(fx - dir * 2, ty + 4, 1, 1, P.skin);
  R(x - 1, wy - 3, 2, 3, P.bot); R(x + (spin ? dir : -dir), fy - 2, 1, 2, P.bot);
  const hx = x - 1 + dir, hy = ty - P.hh;
  R(hx, hy, P.hw, P.hh, P.skin); R(hx, hy, P.hw, 2, P.hair); R(dir > 0 ? hx : hx + P.hw - 1, hy, 1, P.hh - 1 + P.long, P.hair);
  if (P.hat) R(hx, hy - 1, P.hw, 2, P.hat);
  if (ring) { ctx.globalAlpha = 0.7; dot(fx - dir * 2, wy - 7, '#fff8d8'); dot(fx - dir, wy - 8, '#fff8d8'); ctx.globalAlpha = 1; }
}

/* ================= Maple leaves, the city's own weather: three depths, mostly off the red maple and the willow ================= */
const LEAF_SHAPES = [['11', '11'], ['1.1.1', '11111', '.111.', '..1..'],
  ['....1....', '.1.111.1.', '.1111111.', '111111111', '.1111111.', '1.11111.1', '...111...', '....1....', '....1....']];
const LEAF_PALS = [['#c8281c', '#7a1410', '#f05a3a'], ['#e0561e', '#8e2c10', '#f88a44'], ['#e8862a', '#9a4a14', '#fab45a'],
  ['#d8a830', '#8a6a1a', '#f4d06a'], ['#a81830', '#5a0c18', '#d84858']];
const LEAF_L = [
  { count: 46, nMin: 7, nMax: 10, amp: 4, drift: 26, land: [WL + 4, QY - 2] },          // far: out over the water, landing on it
  { count: 30, nMin: 10, nMax: 13, amp: 7, drift: 40, land: [QY + 4, GRASS + 6] },      // mid: onto the path
  { count: 12, nMin: 13, nMax: 16, amp: 12, drift: 58, land: [H + 14, H + 14] },        // near: past the viewer
];
LEAF_L.forEach((L, li) => {
  const r = rng(530 + li);
  L.leaves = [];
  for (let i = 0; i < L.count; i++) L.leaves.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(),
    ks: 36 + Math.floor(r() * 30), p2: r(), kf: 26 + Math.floor(r() * 40), p4: r(), pal: Math.floor(r() * 5), src: r() });
});
let LEAF_SPR;                                       // [palette][depth][front, back]
function makeLeaf(pal, depth, back) {
  const S = LEAF_SHAPES[depth], w = S[0].length, h = S.length, mid = w >> 1, p = pix(w, h);
  S.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] === '1') {
    let col = back ? mixHex(pal[0], pal[1], 0.45) : pal[0];
    if (depth > 0 && i === mid && j > 0) col = pal[1];
    else if (!back && depth > 0 && i < mid && (i + j) % 3 === 0) col = pal[2];
    p.set(i, j, mixHex(col, NIGHT, 0.3 - depth * 0.08));
  } });
  return p.done();
}
/** where a leaf of depth L starts: off the maple (left), the willow (right) or the trees behind the viewer */
const leafX = (src, h) => (src < 0.45 ? -6 + h * 100 : src < 0.65 ? 408 + h * 70 : -20 + h * (W + 10));
function drawLeaves(L, depth, t) {
  const S = LEAF_SHAPES[depth], w = S[0].length, h = S.length;
  for (const l of L.leaves) {
    const { cyc, u } = cycle(t, l.n, l.ph), sw = wave(t, l.ks, l.p2);
    const yEnd = lerp(L.land[0], L.land[1], hash(l.id, cyc, 6)), y0 = l.src < 0.45 ? 20 + hash(l.id, cyc, 7) * 70 : l.src < 0.65 ? 30 + hash(l.id, cyc, 7) * 60 : -12;
    const y = y0 + u * (yEnd - y0) - Math.abs(sw) * (depth + 1) * 1.5;
    const x = leafX(l.src, hash(l.id, cyc, 5)) + u * L.drift + sw * L.amp;
    if (x < -12 || x > W + 12) continue;
    const fl = Math.cos(TAU * (l.kf * t / LOOP + l.p4)), spr = LEAF_SPR[(l.pal + cyc) % 5][depth][fl < 0 ? 1 : 0];
    ctx.globalAlpha = L.land[0] < H && u > 0.9 ? (1 - u) * 10 : 1;
    if (depth === 0) ctx.drawImage(spr, Math.round(x), Math.round(y));
    else { const sh = Math.max(1, Math.round(h * Math.abs(fl))); ctx.drawImage(spr, Math.round(x - (w >> 1)), Math.round(y - (sh >> 1)), w, sh); }
  }
  ctx.globalAlpha = 1;
}
const SKITTER = [];                                 // fallen leaves the gusts tumble along the path and the lawn: 20 px a gust, 25 gusts a loop
{
  const r = rng(532);
  for (let i = 0; i < 34; i++) SKITTER.push({ x: r() * (W + 20), y: QY + 6 + Math.floor(r() * (H - QY - 10)), ph: r(), pal: Math.floor(r() * 5) });
}
function drawSkitter(t) {
  for (const l of SKITTER) {
    const { cyc, u } = cycle(t, 25, l.ph), mv = smooth(0, 0.16, u), x = ((l.x + 20 * (cyc + mv)) % (W + 20)) - 10;
    const hop = u < 0.16 ? Math.round(Math.sin(Math.PI * u / 0.16 * 2) * 1.5) : 0, d = l.y > GRASS ? 1 : 0;
    const spr = LEAF_SPR[l.pal][d][u < 0.16 && step(t, 1920) & 1 ? 1 : 0];
    ctx.globalAlpha = 0.85; ctx.drawImage(spr, Math.round(x), l.y - Math.abs(hop) - spr.height + 1); ctx.globalAlpha = 1;
  }
}
const FLOATERS = [];                                // leaves afloat on the harbour, drifting east on the breeze
{
  const r = rng(531);
  for (let i = 0; i < 28; i++) FLOATERS.push({ y: WL + 6 + Math.floor(r() * (QY - WL - 8)), x: r(), k: 1 + Math.floor(r() * 2), ph: r(), col: LEAF_PALS[Math.floor(r() * 5)][0], big: r() < 0.4 });
}
// a picked weather (site/js/engine/weather.js): Toronto draws its own leaves; rain and snow land on the harbour, the
// path and past the viewer, and catch the lamps and the kiosk's hatch (and the sign's lamps, while it's there)
const WEATHER = weatherPlans(['leaves']);
const WX_LAND = [[WL + 2, QY - 1], [QY + 4, GRASS + 4], [H + 4, H + 4]];
const WX_LIGHTS = [...LAMPS.map(l => ({ x: l.x, y: l.y + 22, rx: 30, ry: 46 })), { x: KIOSK.cx, y: 176, rx: 26, ry: 18 }];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 23, y: BB.y + 8, rx: 30, ry: 16, col: '#ffe8c0' }] };

/* ================= Geese and the raccoon ================= */
const LAWN_GEESE = [{ x: 400, y: 241, r: 10, k: 2, ph: 0.1 }, { x: 426, y: 249, r: 14, k: 1, ph: 0.4 }, { x: 452, y: 239, r: 8, k: 3, ph: 0.7 },
  { x: 466, y: 254, r: 8, k: 2, ph: 0.25 }, { x: 378, y: 255, r: 9, k: 1, ph: 0.85 }];
function drawLawnGoose(g, i, t) {
  const a = TAU * (g.k * t / LOOP + g.ph), x = g.x + g.r * Math.sin(a), dir = Math.cos(a) > 0 ? 1 : 0;
  const look = t >= GEESE.look && t < GEESE.t0 + 6, hiss = t >= DOGW[0].bark[0] && t < DOGW[0].bark[1] + 1 && i < 3;
  const graze = !look && !hiss && hash(step(t, 120), i, 51) < 0.62;
  ctx.fillStyle = 'rgba(8,10,24,0.32)'; ctx.fillRect(Math.round(x) - 7, g.y + 1, 14, 1);
  if (hiss) { ctx.drawImage(GOOSE_SPR.fly[0][1], Math.round(x) - 4, g.y - 8); ctx.drawImage(GOOSE_SPR.graze[1], Math.round(x) - 8, g.y - 12); return; }   // wings out, neck low: hissing at the dog
  const spr = graze ? GOOSE_SPR.graze[dir] : GOOSE_SPR.walk[(step(t, 480) + i) & 1 && Math.abs(Math.cos(a)) > 0.3 ? 1 : 0][dir];
  ctx.drawImage(spr, Math.round(x) - 8, g.y - 12);
  if (look && frac(t + i * 0.37) < 0.25) { ctx.globalAlpha = 0.5; dot(dir ? x + 6 : x - 7, g.y - 14, '#e8ecf4'); dot(dir ? x + 7 : x - 8, g.y - 15, '#e8ecf4'); ctx.globalAlpha = 1; }   // honking up at the flight
}
const WATER_GEESE = [{ x: 262, y: 195 }, { x: 274, y: 197 }, { x: 452, y: 194 }];
function drawWaterGeese(t) {
  const bob = WASH.t0 <= t && t < WASH.t1 ? Math.sin((t - WASH.t0) * 3) * env(t, WASH.t0, WASH.t1, 3) : 0;
  WATER_GEESE.forEach((g, i) => {
    const x = Math.round(g.x + 10 * Math.sin(TAU * (t / LOOP * (i + 1) + i * 0.3))), y = Math.round(g.y + bob * 1.5 + 0.6 * wave(t, 20, i * 0.3));
    const d = Math.cos(TAU * (t / LOOP * (i + 1) + i * 0.3)) > 0 ? 1 : 0;
    ctx.globalAlpha = 0.25; R(x - 5, y + 1, 11, 1, '#8a98c0'); ctx.globalAlpha = 1;
    ctx.drawImage(GOOSE_SPR.swim[d], x - 5, y - 5);
  });
}
// the skein: a V of eleven flying in from the east, circling down onto the harbour by the island, then paddling west
const SKEIN = [[GEESE.t0, 500, 30], [170, 330, 40], [177, 222, 66], [182, 196, 118], [GEESE.land, 214, 186]];
function skeinAt(t) {
  for (let i = 1; i < SKEIN.length; i++) if (t < SKEIN[i][0]) {
    const a = SKEIN[i - 1], b = SKEIN[i], u = (t - a[0]) / (b[0] - a[0]);
    return [lerp(a[1], b[1], u), lerp(a[2], b[2], u)];
  }
  return [SKEIN[SKEIN.length - 1][1], SKEIN[SKEIN.length - 1][2]];
}
function drawSkein(t) {
  if (t < GEESE.t0 || t >= GEESE.t1) return;
  const spread = 1 - 0.45 * smooth(174, GEESE.land, t);
  for (let i = 0; i < 11; i++) {
    const k = (i + 1) >> 1, side = i & 1 ? 1 : -1, td = GEESE.land + i * 0.45;            // its touchdown
    if (t < td) {
      const [lx, ly] = skeinAt(Math.min(t, GEESE.land - 0.01));
      const late = t > GEESE.land ? (t - GEESE.land) : 0;
      const x = lx + k * 7 * spread + late * 6, y = ly + side * k * 3.2 * spread + (late ? late * 2 : 0);
      const f = (step(t, 960) + i * 3) % 4, fr = t > 180 ? 1 : [0, 1, 2, 1][f];
      ctx.drawImage(GOOSE_SPR.fly[fr][0], Math.round(x) - 4, Math.round(y) - 2);
    } else {
      const home = 214 + k * 5 + i * 2, x = home - 300 * smooth(GEESE.down, GEESE.t1, t) * 0.4 - 300 * 0.6 * clamp((t - GEESE.down) / (GEESE.t1 - GEESE.down), 0, 1), y = 186 + (i % 3) * 3 + Math.round(0.6 * wave(t, 24, i * 0.2));
      const ring = t - td;
      if (ring < 2.5) {                                                                    // the splash, then a widening ring
        ctx.globalAlpha = 0.5 * (1 - ring / 2.5); ctx.fillStyle = '#dfe6f4';
        const rr = 2 + ring * 5;
        for (let j = 0; j < 10; j++) { const a = j / 10 * TAU; ctx.fillRect(Math.round(home + Math.cos(a) * rr), Math.round(y + 1 + Math.sin(a) * rr * 0.3), 1, 1); }
        ctx.globalAlpha = 1;
      }
      if (x < -12) continue;
      ctx.globalAlpha = 0.25; R(Math.round(x) - 5, y + 1, 11, 1, '#8a98c0'); ctx.globalAlpha = 1;
      ctx.drawImage(GOOSE_SPR.swim[0], Math.round(x) - 5, y - 5);
    }
  }
}
function drawRaccoon(t) {
  const { t0, t1 } = RACCOON;
  if (t < t0 || t >= t1) return;
  const s = along([[t0, 478], [t0 + 10, BIN.x + 8], [t0 + 22, BIN.x + 8], [t1, 478]], t), up = t >= t0 + 11 && t < t0 + 21;
  const y = s.x > BIN.x + 30 ? lerp(BIN.y + 1, 236, clamp((s.x - BIN.x - 30) / 40, 0, 1)) : BIN.y + 1, back = t > t0 + 21;
  if (up) ctx.drawImage(COON_SPR.up, BIN.x + 6, BIN.y - 10);
  else ctx.drawImage(COON_SPR.walk[(step(t, 960) & 1) * (s.moving ? 1 : 0)][back ? 1 : 0], Math.round(s.x) - 6, Math.round(y) - 6);
}
const binLid = t => (t >= RACCOON.t0 + 14 && t < RACCOON.t0 + 19 && (step(t, 960) & 3) < 2 ? 1 : 0);
const DOG_ROWS = [
  ['........ee..', '.......edddn', 't......ddd..', '.tlllllllll.', '..dddddddd..', '..d.d..d.d..', '..d.d..d.d..'],
  ['........ee..', '.......edddn', 't......ddd..', '.tlllllllll.', '..dddddddd..', '...dd..dd...', '..d..d.d..d.'],
  ['.......ee...', '......edddn.', 't.....dddd..', '.tlllllllll.', '..dddddddd..', '..d.d..d.d..', '..d.d..d.d..'],
];
const DOG_KEY = { d: '#2a2420', l: '#4a3c30', t: '#2a2420', e: '#1a1410', n: '#0a0808' };

/* ================= The cast ================= */
const showOn = t => inWin(t, SHOW1.t0, SHOW1.t1) || inWin(t, SHOW2.t0, SHOW2.t1);
function drawCouple(t) {
  const [he, she] = SPOTS.couple, fy = BENCH.y - 6, kiss = inWin(t, KISS[0], KISS[1]);
  const pt = POINTS.some(([a, b]) => inWin(t, a, b)) && frac(t / 6) < 0.7, lean = !kiss && frac(t / 40 + 0.2) < 0.2;
  if (kiss) {
    person(she, fy, L_SHE, { view: 2, dir: -1, sit: true, lean: -2 });
    person(he, fy, L_HE, { view: 2, dir: 1, sit: true, aR: 3, lean: 2 });
  } else {
    person(she, fy, L_SHE, { view: 0, sit: true, aL: pt ? 4 : 0, lean: lean ? -1 : 0 });   // she points up at the tower
    person(he, fy, L_HE, { view: 0, sit: true, aR: 5, lean: lean ? 1 : 0 });
  }
}
function drawPhoto(t) {
  const { x, tri } = SPOTS.photo, fy = EDGE + 1, u = frac(t / 24 + 0.3), busy = showOn(t) || inWin(t, FERRY.t0 + 6, FERRY.t1 - 20);
  ctx.fillStyle = '#20222c';                                                                // the tripod, the camera on it
  for (const lx of [tri - 3, tri, tri + 3]) { const n = fy - 189; for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(tri, lx, k / n)), 189 + k, 1, 1); }
  R(tri - 1, 186, 4, 3, '#121218'); R(tri - 1, 186, 3, 1, '#3a3c4a');
  if (u < 0.6 || busy && u < 0.85) {
    if (frac(t) < 0.5) { dot(tri + 2, 187, '#ff3030'); halo(HL.red, tri + 2, 187, 0.5); }   // exposing
    person(x, fy, L_PHOTO, { view: 2, dir: -1, bend: true, aR: 2 });
  } else if (u < 0.88) {
    person(x, fy, L_PHOTO, { view: 2, dir: -1, aR: 3 });
    const ty = tyOf(L_PHOTO, fy);
    R(x - 5, ty + 1, 2, 2, '#9ad0ff'); halo(HL.screen, x - 4, ty + 2, 0.9);                 // checking the shot
  } else person(x, fy, L_PHOTO, { view: 0 });
}
function drawFriends(t) {
  const [a, b] = SPOTS.friends, fy = QY + 1, eatA = frac(t / 30 + 0.1), eatB = frac(t / 30 + 0.6);
  R(a + 3, QY - 2, 5, 3, '#e8e0d0'); R(a + 3, QY - 2, 5, 1, '#c02a2a');                      // the box of butter tarts between them
  person(a, fy, L_FRA, { view: 0, sit: true, aR: eatA < 0.08 ? 3 : eatA < 0.2 ? 2 : 0, lean: frac(t / 20) < 0.15 ? 1 : 0 });
  person(b, fy, L_FRB, { view: 0, sit: true, aL: eatB < 0.08 ? 3 : eatB < 0.2 ? 2 : 0 });
  if (eatA >= 0.08 && eatA < 0.2) dot(a + 2, tyOf({ ll: 0, th: L_FRA.th }, fy) - 1, '#d8a050');   // a tart on its way
  for (let k = 0; k < 3; k++) {                                                             // steam off his thermos cup
    const { u } = cycle(t, 300, k / 3);
    ctx.globalAlpha = 0.3 * (1 - u); dot(b + 5 + wave(t, 40, k * 0.3), QY - 4 - u * 6, '#dfe4f0'); ctx.globalAlpha = 1;
  }
  R(b + 4, QY - 3, 1, 2, '#c8ccd8');
}
function drawKid(t) {
  const fy = EDGE + 2, x = SPOTS.kid, coon = inWin(t, RACCOON.t0 + 6, RACCOON.t0 + 24), wv = inWin(t, WAVE[0], WAVE[1]);
  const sky = inWin(t, GEESE.t0 + 2, GEESE.land + 2), s = step(t, 480);
  person(SPOTS.parent, fy, L_PARENT, { view: coon ? 1 : 0, aL: sky && s % 6 < 3 ? 4 : 0, aR: frac(t / 30) < 0.12 ? 2 : 0 });
  if (coon) person(x, fy, L_KID, { view: 1, aR: 3 });                                        // turned round, pointing at the raccoon
  else if (wv) person(x, fy, L_KID, { view: 0, aL: 1, aR: s % 4 < 2 ? 1 : 3 });               // waving at the ferry
  else if (sky) person(x, fy, L_KID, { view: 0, aL: 4 });                                   // pointing at the geese
  else { const h = hash(step(t, 60), 7); person(x, fy, L_KID, { view: h < 0.2 ? 1 : 0, aR: h > 0.8 ? 3 : 0, lean: h > 0.5 && h < 0.6 ? 1 : 0 }); }
}
function drawFisher(t) {
  const x = SPOTS.fisher, fy = EDGE + 1, u = frac(t / 48 + 0.15), ty = tyOf(L_FISH, fy);
  const cast = u < 0.08, reel = u > 0.7 && u < 0.85, c = cast ? Math.sin(Math.PI * u / 0.08) : 0;
  person(x, fy, L_FISH, { view: 0, aL: 3 });
  const hx = x - 5, hy = ty + 2, tipX = Math.round(x - 17 + c * 22), tipY = Math.round(ty - 15 - c * 4);
  const n = Math.max(Math.abs(tipX - hx), Math.abs(tipY - hy));
  for (let k = 0; k <= n; k++) dot(lerp(hx, tipX, k / n), lerp(hy, tipY, k / n), k < n * 0.3 ? '#3a2e24' : '#8a7c62');   // the rod, catching the lamp
  const lx = x - 26, ly = QY - 7, m = Math.max(1, Math.abs(ly - tipY));
  ctx.globalAlpha = 0.45; ctx.fillStyle = '#d8dce8';                                         // the line, down to the float
  for (let k = 1; k <= m; k++) ctx.fillRect(Math.round(lerp(tipX, lx, k / m) + Math.sin(Math.PI * k / m) * (reel ? 0.5 : 2)), Math.round(lerp(tipY, ly, k / m)), 1, 1);
  ctx.globalAlpha = 1;
  dot(lx, ly, '#ff5a3a'); dot(lx, ly + 1, '#e8e4dc');                                       // the float
  if (u > 0.08 && u < 0.14) { ctx.globalAlpha = 0.5 * (1 - (u - 0.08) / 0.06); const rr = 1 + (u - 0.08) * 60; for (let j = 0; j < 8; j++) { const a = j / 8 * TAU; dot(lx + Math.cos(a) * rr, ly + 1 + Math.sin(a) * rr * 0.3, '#c8d4ec'); } ctx.globalAlpha = 1; }
  if (reel) dot(hx + 1, hy + 2, '#8a8c96');                                                  // the reel turning
}
function drawPhone(t) {
  const x = SPOTS.phone, fy = EDGE + 1, film = showOn(t) || inWin(t, RACCOON.t0 + 8, RACCOON.t0 + 22);
  const ty = tyOf(L_PHONE, fy);
  if (film) {
    person(x, fy, L_PHONE, { view: inWin(t, RACCOON.t0, RACCOON.t1) ? 1 : 0, aL: 2, aR: 2 });
    R(x - 1, ty - 4, 3, 2, '#9ad0ff'); halo(HL.screen, x, ty - 3, 0.8);
  } else person(x, fy, L_PHONE, { view: 0, aR: 2 });
}
function drawDogWalker(t) {
  for (const D of DOGW) {
    if (t < D.t0 || t >= D.t1) continue;
    const d = D.dir, keys = D.bark ? [[D.t0, -24], [D.bark[0], 362], [D.bark[1] + 1, 362], [D.t1, W + 40]] : [[D.t0, W + 24], [D.t1, -40]];
    const s = along(keys, t), x = Math.round(s.x), dx = x + d * 14, barking = D.bark && inWin(t, D.bark[0], D.bark[1]);
    const df = barking ? 2 : s.moving ? (Math.floor(x / 3) & 1) : 0;
    ctx.fillStyle = 'rgba(8,10,24,0.3)'; ctx.fillRect(dx - 4, LANE_A + 1, 9, 1);
    ctx.drawImage(DOG_SPR[df][d > 0 ? 0 : 1], dx - 6, LANE_A - 6 - (barking && frac(t * 2) < 0.3 ? 1 : 0));
    if (barking && frac(t * 2) < 0.4) { ctx.globalAlpha = 0.5; dot(dx + d * 7, LANE_A - 6, '#e4eaf6'); dot(dx + d * 8, LANE_A - 7, '#e4eaf6'); ctx.globalAlpha = 1; }
    person(x, LANE_A, L_DOGW, { view: 2, dir: d, step: s.moving && (Math.floor(x / 4) & 1), aR: 3 });
    const hx = x + d * 4, hy = tyOf(L_DOGW, LANE_A) + 2, cx = dx + d * 2, cy = LANE_A - 4, n = Math.abs(cx - hx);
    ctx.fillStyle = '#c8323a';                                                               // the leash
    for (let k = 1; k < n; k++) ctx.fillRect(hx + Math.sign(cx - hx) * k, Math.round(lerp(hy, cy, k / n) + Math.sin(Math.PI * k / n) * 2), 1, 1);
  }
}
function drawBikes(t, crowd) {
  BIKES.forEach((B, bi) => {
    if (t < B.t0 || t >= B.t1 || !here(bi ? RANK.bike2 : RANK.bike1, crowd)) return;
    const u = (t - B.t0) / (B.t1 - B.t0), x = B.dir > 0 ? lerp(-30, W + 30, u) : lerp(W + 30, -30, u);
    for (let i = 0; i < B.n; i++) drawBike(Math.round(x - B.dir * i * 22), LANE_B - i * 3, B.dir, L_BIKE[bi + i], t, inWin(t, B.bell, B.bell + 0.8) && i === 0);
  });
}
function drawJogger(t) {
  for (const J of JOG) {
    if (t < J.t0 || t >= J.t1) continue;
    const u = (t - J.t0) / (J.t1 - J.t0), d = J.dir, x = Math.round(d > 0 ? lerp(-20, W + 20, u) : lerp(W + 20, -20, u)), f = Math.floor(x / 5) & 1;
    const hy = person(x, LANE_B - f, L_JOG, { view: 2, dir: d, step: f, aR: 4 });
    for (let k = 0; k < 2; k++) {                                                            // breath in the cold air
      const { u: v } = cycle(t, 300, k / 2);
      ctx.globalAlpha = 0.4 * (1 - v); dot(x + d * (4 + v * 6), hy + 3 - v * 2, '#e4eaf6'); ctx.globalAlpha = 1;
    }
  }
}
function drawStroll(t) {
  if (t < STROLL.t0 || t >= STROLL.t1) return;
  const x = Math.round(lerp(W + 24, -30, (t - STROLL.t0) / (STROLL.t1 - STROLL.t0))), f = Math.floor(x / 4) & 1;
  person(x + 8, LANE_A - 1, L_STROLL_B, { view: 2, dir: -1, step: !f });
  person(x, LANE_A, L_STROLL_A, { view: 2, dir: -1, step: f, aR: 3 });                       // hand in hand
}
function drawVendor(t) {
  const wipe = frac(t / 20 + 0.3) < 0.3, x = KIOSK.cx - 3 + (wipe ? Math.round(3 * wave(t, 152)) : 0), top = HATCH.y0 + 6;
  R(x - 3, top + 5, 7, HATCH.y1 - top - 4, VENDOR.top); R(x - 3, top + 5, 7, 1, mixHex(VENDOR.top, '#ffe0a0', 0.4));
  R(x - 2, top, 4, 5, VENDOR.skin); R(x - 2, top, 4, 2, VENDOR.hair); R(x - 2, top - 1, 4, 2, VENDOR.hat);
  if (wipe) R(x + 3, HATCH.y1, 3, 1, '#e8e4dc');                                            // the cloth on the counter
  for (let k = 0; k < 3; k++) {                                                             // the urn steams
    const { u } = cycle(t, 240, k / 3);
    ctx.globalAlpha = 0.35 * (1 - u); dot(HATCH.x1 - 7 + wave(t, 36, k * 0.4), HATCH.y0 + 8 - u * 7, '#fff4e0'); ctx.globalAlpha = 1;
  }
}

/* ================= Prerendered state ================= */
let HL, SKY_C, WISPS, BACK_C, CN_C, CN_TINT, FRONT_C, WATER_C, MIRROR_C, KIOSK_C, BOARD_C, AD_C, PITCH, GROUND_C, LAMP_C, BENCH_C,
  BIN_C, MAPLE_C, WILLOW_C, FERRY_C, TAXI_C, PLANE_C, GOOSE_SPR, COON_SPR, DOG_SPR, VIG_C;
const SPLIT = {};                                   // where each layer's switching windows start and end in WINS
const TOP_LAMPS = [];                               // red aircraft lamps on the roofs: [x, y]
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.48) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [5, 7, 18], Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  for (const L of [WINS, QQ_LAMPS, MASTS, MAPLE_TIPS, TOP_LAMPS]) L.length = 0;
  HL = {
    cnPod: makeHalo(26, '#ffe2b0', 0.2, 1.6), cnShaft: makeHalo(54, '#b8c4e8', 0.08, 1.6), cnSky: makeHalo(9, '#fff0d0', 0.3, 1.4),
    cnShow: makeHalo(60, '#ffffff', 0.1, 1.6), red: makeHalo(3, '#ff3a30', 0.5, 1), redS: makeHalo(2, '#ff3a30', 0.4, 1), redBig: makeHalo(5, '#ff3a30', 0.4, 1.2),
    rbp: makeHalo(30, '#ffc860', 0.1, 1.6), crown: makeHalo(8, '#e8f0ff', 0.16, 1.4), city: makeHalo(70, '#ffb070', 0.05, 1.5),
    lamp: makeHalo(44, '#ffc878', 0.17, 1.7), lampCore: makeHalo(10, '#fff2d0', 0.36, 1.3), qlamp: makeHalo(5, '#ffd890', 0.35, 1.2),
    tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), hatch: makeHalo(30, '#ffb860', 0.16, 1.5), sign: makeHalo(22, '#ffe2b0', 0.14, 1.5),
    land: makeHalo(12, '#f4f6ff', 0.4, 1.3), screen: makeHalo(4, '#9ad0ff', 0.4, 1.2), ferry: makeHalo(26, '#ffd890', 0.14, 1.5),
  };
  SKY_C = renderSky();
  WISPS = [makeWisps(11, 70, 8, '#2a2c4c', '#46405e', 150), makeWisps(29, 60, 7, '#34324e', '#584862', 130)];
  const r = rng(1923);
  SPLIT.back = WINS.length;
  {
    const p = pix(W, WL);
    paintMidtown(p, r);
    for (const b of BACK_TOWERS) paintBldg(p, r, b);
    paintRBP(p);
    for (const b of MID_TOWERS) paintBldg(p, r, b);
    paintSkyTower(p, r);
    paintCranes(p);
    for (const b of CITYPLACE) paintBldg(p, r, b);
    paintDome(p);
    BACK_C = trim(p);
    for (const b of [...BACK_TOWERS, ...MID_TOWERS]) if (b.beaconAt) TOP_LAMPS.push(b.beaconAt);
    TOP_LAMPS.push([SKYT.x0 + 1, SKYT.top + 1], [SKYT.x1 - 2, SKYT.top + 1], [SKYT.x0, 110], [SKYT.x1 - 1, 110]);
    for (const c of CRANES) TOP_LAMPS.push([c.x + c.r, c.jib - 1], [c.x, c.jib - 3]);
  }
  SPLIT.front = WINS.length;
  {
    const p = pix(W, WL);
    for (const [x0, x1, top, ramp, win, lit] of FRONT) paintBldg(p, r, { x0, x1, top, ramp, win, lit: lit * 0.72, haze: 0.06, crown: 'flat' });
    paintQQT(p, r); paintWestin(p, r); paintShore(p, r);
    FRONT_C = trim(p);
  }
  SPLIT.end = WINS.length;
  CN_C = renderCN(CN_PLAIN);
  CN_TINT = {};
  for (const [k, ramp] of Object.entries(SHOW_COLS)) CN_TINT[k] = renderCN(ramp);
  WATER_C = renderWater();
  {                                                                                          // the mirror's source: the still far side
    const [c, x] = makeCanvas(W, WL);
    x.drawImage(SKY_C, 0, 0);
    for (const L of [BACK_C, CN_C, FRONT_C]) x.drawImage(L.c, L.x, L.y);
    MIRROR_C = renderMirror(c);
  }
  KIOSK_C = renderKiosk(); BOARD_C = renderBoard(); AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  GROUND_C = renderGround(); LAMP_C = renderLamp(); BENCH_C = renderBench(); BIN_C = [renderBin(0), renderBin(1)];
  MAPLE_C = renderMaple(); WILLOW_C = renderWillow(); STRAND_C = STRANDS.map(renderStrand);
  FERRY_C = renderFerry();
  TAXI_C = [sprite(TAXI_ROWS, TAXI_KEY), sprite(TAXI_ROWS, TAXI_KEY, true)];
  PLANE_C = sprite(PLANE_ROWS, PLANE_KEY);
  const two = rows => [sprite(rows, GOOSE_KEY), sprite(rows, GOOSE_KEY, true)];
  GOOSE_SPR = { walk: GOOSE_WALK.map(two), graze: two(GOOSE_GRAZE), swim: two(GOOSE_SWIM), fly: GOOSE_FLY.map(two) };
  COON_SPR = { walk: COON_WALK.map(rows => [sprite(rows, COON_KEY), sprite(rows, COON_KEY, true)]), up: sprite(COON_UP, COON_KEY) };
  DOG_SPR = DOG_ROWS.map(rows => [sprite(rows, DOG_KEY), sprite(rows, DOG_KEY, true)]);
  LEAF_SPR = LEAF_PALS.map(pal => [0, 1, 2].map(d => [makeLeaf(pal, d, false), makeLeaf(pal, d, true)]));
  VIG_C = renderVignette();
}

/* ================= Per-frame: the sky, the skyline ================= */
const JETS = [{ k: 4, ph: 0.1, y0: 18, y1: 30, dir: -1 }, { k: 3, ph: 0.55, y0: 8, y1: 4, dir: 1 }];
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.4 + 0.6 * Math.max(0, wave(t, s.k, s.ph))); ctx.fillStyle = s.c; ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  WISPS.forEach((c, i) => { const off = scroll(t, i + 1, W), y = i ? 40 : -6; ctx.drawImage(c, -off, y); ctx.drawImage(c, W - off, y); });
  for (const j of JETS) {                                                                    // airliners far overhead, blinking
    const u = frac(j.k * t / LOOP + j.ph);
    if (u > 0.4) continue;
    const v = u / 0.4, x = j.dir > 0 ? lerp(-10, W + 10, v) : lerp(W + 10, -10, v), y = lerp(j.y0, j.y1, v);
    dot(x, y, '#c8ccd8'); if (frac(t * 0.9 + j.ph) < 0.1) { dot(x, y + 1, '#ff4a40'); }
    if (frac(t * 0.7 + j.ph * 3) < 0.06) dot(x + 1, y, '#ffffff');
  }
}
const winLit = (w, t) => frac(w.k * t / LOOP + w.ph) < w.duty;
function drawWins(a, b, t) {
  for (let i = a; i < b; i++) { const w = WINS[i]; if (winLit(w, t)) R(w.x, w.y, w.w, w.h, w.c); }
}
function drawBack(t) {
  halo(HL.city, 310, 150, 1);
  halo(HL.rbp, 350, 140, 0.9 + 0.1 * wave(t, 6));
  blit(BACK_C);
  drawWins(SPLIT.back, SPLIT.front, t);
  for (const x of [327, 334, 216]) halo(HL.crown, x, x === 216 ? 125 : 105, 0.7);
  const b = beacon(t);
  for (const [x, y] of TOP_LAMPS) { ctx.globalAlpha = 0.35 + 0.65 * b; dot(x, y, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.red, x, y, b); }
  for (const c of CRANES) {                                                                  // the trolley runs out along the jib and back, a hook under it
    const u = c.trolley[0] + (c.trolley[1] - c.trolley[0]) * (0.5 - 0.5 * Math.cos(TAU * c.k * t / LOOP)), tx = Math.round(c.x + 2 + u * (c.r - 3));
    R(tx - 1, c.jib + 1, 3, 1, '#3a3428');
    const hl = 6 + Math.round(4 * (0.5 + 0.5 * wave(t, c.k * 2, 0.3)));
    ctx.globalAlpha = 0.6; R(tx, c.jib + 2, 1, hl, '#6a6458'); ctx.globalAlpha = 1; R(tx - 1, c.jib + 2 + hl, 3, 1, '#c8a040');
  }
  ctx.fillStyle = '#fff4dc';
  for (const x of DOME_LIGHTS) { ctx.globalAlpha = 0.7 + 0.3 * wave(t, 60, x * 0.03); ctx.fillRect(x, 156, 1, 1); }
  ctx.globalAlpha = 1;
}
/* the CN Tower's light: plain cool white, a soft sheen climbing the shaft every 30 s; the shows paint bands of colour */
function cnShow(t) {
  // returns [{ col, a, y0, y1 }] bands over the plain look (y rows from the tower's foot up), or null
  if (inWin(t, SHOW1.t0, SHOW1.t1)) {
    const s = t - SHOW1.t0, len = SHOW1.t1 - SHOW1.t0, fade = 1 - smooth(len - 5, len, s);
    if (s < 6) return [{ col: 'red', a: fade, y0: WL - (WL - CN.top) * smooth(0, 6, s), y1: WL }];   // the red climbs from the foot
    if (s < len - 8) {                                                                       // red, with white bands climbing it, one every 8 s
      const bands = [{ col: 'red', a: fade, y0: CN.top, y1: WL }], h = 9;
      for (let k = 0; k < 4; k++) {
        const v = (s - 6) / 8 - k;
        if (v < 0 || v > 1) continue;
        const y = WL - v * (WL - CN.top + h);
        bands.push({ col: 'white', a: fade * Math.sin(Math.PI * v) ** 0.5, y0: y, y1: y + h });
      }
      return bands;
    }
    return [{ col: 'red', a: fade, y0: CN.top, y1: WL }];
  }
  if (inWin(t, SHOW2.t0, SHOW2.t1)) {
    const s = t - SHOW2.t0, len = SHOW2.t1 - SHOW2.t0, a = 0.85 * smooth(0, 4, s) * (1 - smooth(len - 4, len, s)), bands = [], h = 10;
    const off = (s * 5) % (h * RAINBOW.length);
    for (let y = WL + h * RAINBOW.length - off, i = 0; y > CN.top - h; y -= h, i++) bands.push({ col: RAINBOW[i % RAINBOW.length], a, y0: y - h, y1: y });
    return bands;
  }
  return null;
}
const SHOW_HALO = { red: '#ff3040', white: '#ffffff', orange: '#ff9030', yellow: '#ffe040', green: '#40e070', cyan: '#40d0e0', blue: '#4060ff', violet: '#a050ff' };
let showTint = 'white';
function drawCN(t) {
  halo(HL.cnShaft, CN.x, 120, 0.9);
  blit(CN_C);
  const bands = cnShow(t);
  showTint = null;
  if (bands) {
    for (const b of bands) {
      const y0 = Math.max(CN.top, Math.round(b.y0)), y1 = Math.min(WL, Math.round(b.y1));
      if (y1 <= y0 || b.a <= 0.01) continue;
      const src = CN_TINT[b.col];
      ctx.globalAlpha = b.a;
      ctx.drawImage(src.c, 0, y0 - src.y, src.c.width, y1 - y0, src.x, y0, src.c.width, y1 - y0);
      if (y0 <= 90 && y1 >= 90) showTint = b.col;
    }
    ctx.globalAlpha = 1;
    if (!showTint) showTint = bands[bands.length - 1].col;
  } else {                                                                                   // the sheen, a few rows brighter, climbing
    const y = Math.round(WL - frac(t / 30) * (WL - CN.top));
    ctx.globalAlpha = 0.35 * Math.sin(Math.PI * frac(t / 30));
    const src = CN_TINT.white;
    ctx.drawImage(src.c, 0, y - src.y, src.c.width, 6, src.x, y, src.c.width, 6);
    ctx.globalAlpha = 1;
  }
  halo(HL.cnPod, CN.x, 73, 0.9 + 0.1 * wave(t, 12));
  halo(HL.cnSky, CN.x, 47, 0.8);
  const b = beacon(t);
  for (const y of [21, 29, 37]) { ctx.globalAlpha = 0.35 + 0.65 * b; dot(CN.x, y, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.redS, CN.x, y, b); }
  const rot = step(t, 48);                                                                   // the restaurant turns, slowly
  dot(CN.x - 3 + (rot % 7), 71, '#fff4d8');
}
const STREETCAR = [{ k: 6, ph: 0.1, dir: 1 }, { k: 6, ph: 0.6, dir: -1 }];
const QQ_CARS = [[1, 9, 0.12], [-1, 8, 0.4], [1, 7, 0.7], [-1, 10, 0.9], [1, 11, 0.33]];
function drawFront(t) {
  blit(FRONT_C);
  drawWins(SPLIT.front, SPLIT.end, t);
  for (const x of QQ_LAMPS) { dot(x, WL - 4, '#ffe8b8'); halo(HL.qlamp, x, WL - 4, 0.8); }
  for (const [x, y] of MASTS) dot(x, y, '#fff4e0');
  for (const [d, k, ph] of QQ_CARS) {                                                        // cars along Queens Quay, between the trees
    const x = Math.round(lerp(d > 0 ? -4 : W + 4, d > 0 ? W + 4 : -4, frac(k * t / LOOP + ph)));
    if (hash(x >> 3, 4, 61) < 0.35) continue;
    dot(x, WL - 3, d > 0 ? '#fff2d8' : '#ff4a3a');
  }
  for (const s of STREETCAR) {                                                               // a red streetcar on Queens Quay
    const u = frac(s.k * t / LOOP + s.ph);
    if (u > 0.5) continue;
    const x = Math.round(s.dir > 0 ? lerp(150, 345, u / 0.5) : lerp(345, 150, u / 0.5));
    R(x - 7, WL - 7, 15, 4, '#a01c1c'); R(x - 7, WL - 7, 15, 1, '#d8d4d0');
    for (let i = -6; i < 7; i += 2) dot(x + i, WL - 6, '#ffe6b0');
    dot(s.dir > 0 ? x + 7 : x - 7, WL - 5, '#fff4d8');
    ctx.fillStyle = '#2a2a30'; ctx.fillRect(x - 1, WL - 9, 1, 2);                            // the pantograph
  }
}

/* ================= Per-frame: the plane ================= */
function drawPlane(t) {
  for (const P of PLANES) {
    if (t < P.t0 || t >= P.t1) continue;
    const u = (t - P.t0) / (P.t1 - P.t0), x = Math.round(lerp(W + 30, -40, u)), y = Math.round(lerp(103, 142, u * u * 0.4 + u * 0.6));
    halo(HL.land, x + 2, y + 5, 0.9);
    ctx.globalAlpha = 0.18; ctx.fillStyle = '#e8ecff';                                      // the landing lights' beam, ahead
    for (let k = 1; k < 18; k++) ctx.fillRect(x - k, y + 5 + Math.round(k * 0.18), 1, 1 + (k >> 3));
    ctx.globalAlpha = 1;
    ctx.drawImage(PLANE_C, x, y);
    for (const wx of PLANE_WINDOWS) dot(x + wx, y + 4, '#ffd890');
    dot(x + 2, y + 5, '#ffffff'); dot(x + 9, y + 5, '#ffffff');
    dot(x + 11, y + 2, '#ff3a30');                                                           // the near wingtip's red light
    if (frac(t) < 0.12) { dot(x + 12, y + 1, '#ff3a30'); halo(HL.red, x + 12, y + 1, 1); }  // the beacon
    if (frac(t / 1.5 + 0.4) < 0.05) dot(x + 7, y + 2, '#ffffff');
  }
}
function planeStreak(t) {
  for (const P of PLANES) if (t >= P.t0 && t < P.t1) {
    const u = (t - P.t0) / (P.t1 - P.t0), x = Math.round(lerp(W + 30, -40, u));
    streak(ctx, t, x + 2, 3, '#e8ecff', 0.16, 31, WL + 1, QY);
  }
}

/* ================= Per-frame: the water, and what floats on it ================= */
function drawWater(t) {
  ctx.drawImage(WATER_C, 0, WL);
  const rows = QY - WL;
  for (let k = 0; k < rows; k++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.6 + k * 0.07) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.6);
    ctx.globalAlpha = 0.9 * (1 - k / (rows + 16)) * (0.84 + 0.16 * Math.sin(TAU * 60 * t / LOOP + k * 1.7));
    ctx.drawImage(MIRROR_C, 0, k, W, 1, off, WL + k, W, 1);
  }
  for (const g of GLINTS) {
    const v = wave(t, g.kf, g.ph);
    if (v < -0.1) continue;
    ctx.globalAlpha = 0.5 * Math.min(1, v + 0.1);
    ctx.fillStyle = g.c; ctx.fillRect(Math.round(frac(g.x + g.k * t / LOOP) * (W + 30)) - 15, g.y, g.len, 1);
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, QY);
  const sc = showTint ? SHOW_HALO[showTint] : '#e8ecf8';
  streak(ctx, t, CN.x, 5, sc, showTint ? 0.34 : 0.26, 1, WL + 1, QY);                         // the CN Tower's long reflection
  streak(ctx, t, CN.x, 9, showTint ? sc : '#9aa4c4', 0.12, 13, WL + 1, QY);
  for (const L of LAMPS) streak(ctx, t, L.x, 3, '#ffd890', 0.22, L.x, QY - 12, QY);          // the island's lamps, close by
  planeStreak(t);
}
function drawFloaters(t) {
  const bob = WASH.t0 <= t && t < WASH.t1 ? env(t, WASH.t0, WASH.t1, 3) : 0;
  for (const f of FLOATERS) {
    const x = Math.round(frac(f.x + f.k * t / LOOP) * (W + 20)) - 10, y = f.y + Math.round(0.6 * wave(t, 30, f.ph) + bob * Math.sin(t * 3 + f.x * 9));
    ctx.fillStyle = mixHex(f.col, NIGHT, 0.45); ctx.fillRect(x, y, f.big ? 3 : 2, 1);
    if (f.big) ctx.fillRect(x + 1, y - 1, 1, 1);
  }
}
function drawWake(x, wy, dir, n, t, spread) {
  const st = step(t, 1440);
  for (let k = 0; k < n; k++) {
    if (hash(k, st, 17) > 0.84 - k * 0.01) continue;
    ctx.globalAlpha = 0.5 * (1 - k / n);
    dot(x - dir * k * 3, wy + 1 + k * spread, k < 5 ? '#dfe6f4' : '#8a98c0');
    if (spread > 0.2) dot(x - dir * k * 3, wy + 1 - k * spread * 0.4, '#6a78a0');
  }
  ctx.globalAlpha = 1;
}
function drawFerry(t, crowd) {
  const { t0, t1, wy } = FERRY;
  if (t < t0 || t >= t1) return;
  const u = (t - t0) / (t1 - t0), x = Math.round(lerp(W + 6, -FRY.w - 8, u)), y0 = wy - FRY.h + 1;
  halo(HL.ferry, x + 22, wy - 8, 0.9);
  drawWake(x + FRY.w, wy, -1, 34, t, 0.28);
  for (let j = 0; j < 12; j++) {                                                             // its mirror image, broken
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 2.3) * (0.5 + j * 0.12));
    ctx.globalAlpha = 0.28 * (1 - j / 12);
    ctx.drawImage(FERRY_C, 0, FRY.h - 1 - j, FRY.w, 1, x + off, wy + 1 + j, FRY.w, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(FERRY_C, x, y0);
  const s = step(t, 160);
  FERRY_BULBS.forEach((bx, i) => { if (hash(i, s, 5) > 0.1) dot(x + bx, y0 + 3, ['#ffe6a0', '#fff4d8', '#ff8a6a'][i % 3]); });
  if (crowd > 0) for (const f of FERRY_FOLK) {                                               // the passengers at the rail, waving back at the kid
    const wave2 = f.wave && inWin(t, WAVE[0] + 1, WAVE[1]) && (step(t, 480) + f.x) % 4 < 2;
    dot(x + f.x, y0 + 4, f.skin); dot(x + f.x, y0 + 5, f.coat);
    if (wave2) dot(x + f.x + 1, y0 + 3, f.skin);
  }
  dot(x, wy - 5, '#ff3a30'); dot(x + 21, y0, '#ffffff'); halo(HL.tiny, x + 21, y0, 0.9);   // its masthead and side lights
}
function drawWash(t) {
  if (t < WASH.t0 || t >= WASH.t1) return;
  const s = t - WASH.t0, len = WASH.t1 - WASH.t0;
  for (let k = 0; k < 4; k++) {                                                              // the ferry's wash running in towards the seawall
    const yy = Math.round(lerp(FERRY.wy + 3, QY - 1, frac(s / 4.5 + k / 4)));
    ctx.globalAlpha = 0.35 * env(t, WASH.t0, WASH.t1, 3);
    ctx.fillStyle = '#9aa8cc';
    for (let x = 0; x < W; x += 3) if (hash(x >> 2, k, Math.floor(s * 2)) < 0.5) ctx.fillRect(x, yy, 2, 1);
  }
  ctx.globalAlpha = 1;
  for (let k = 0; k < 18; k++) {                                                             // and slapping it: spray along the edge
    const ph = frac(s / 1.5 + hash(k, 3, 71));
    if (ph > 0.3) continue;
    const x = Math.round(hash(k, Math.floor(s / 1.5), 72) * W), hgt = Math.sin(Math.PI * ph / 0.3) * 3;
    ctx.globalAlpha = 0.6 * (1 - s / len * 0.6);
    dot(x, QY - 1 - hgt, '#e8eef8'); dot(x + 1, QY - hgt * 0.6, '#c8d4ec');
  }
  ctx.globalAlpha = 1;
}
function drawTaxi(t) {
  for (const T of TAXI) {
    if (t < T.t0 || t >= T.t1) continue;
    const u = (t - T.t0) / (T.t1 - T.t0), x = Math.round(T.dir > 0 ? lerp(-20, W + 6, u) : lerp(W + 6, -20, u)), wy = 192;
    const spr = TAXI_C[T.dir > 0 ? 0 : 1];
    drawWake(T.dir > 0 ? x : x + 14, wy, T.dir, 16, t, 0.16);
    ctx.globalAlpha = 0.3; ctx.drawImage(spr, 0, 4, 14, 3, x, wy + 1, 14, 3); ctx.globalAlpha = 1;
    ctx.drawImage(spr, x, wy - 6);
    dot(T.dir > 0 ? x + 7 : x + 6, wy - 6, '#ffffff'); halo(HL.tiny, T.dir > 0 ? x + 7 : x + 6, wy - 6, 0.9);
    dot(T.dir > 0 ? x + 13 : x, wy - 3, T.dir > 0 ? '#40ff70' : '#ff3a30');
  }
}

/* ================= Per-frame: the island's side ================= */
const BB_FLICK = [[47.2, 48.4], [163.6, 164.3]];    // one of the sign's lamps stutters now and then
function drawBoard(t, pitch) {
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.sign, BB.x + 23, BB.y + 9, fl ? 0.55 : 0.95);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BB.x, BB.y);
    if (fl) { ctx.globalAlpha = 0.3; R(BB.x + BB.w / 2, BB.y, BB.w / 2, BB.h, '#0a0c18'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const [lx, on] of [[BB.x + 13, true], [BB.x + BB.w - 8, !fl]]) { dot(lx, BB.y - 6, on ? '#fff6dc' : '#6a6050'); if (on) halo(HL.tiny, lx, BB.y - 5, 0.9); }
}
function drawWillow(t) {
  blit(WILLOW_C);
  STRANDS.forEach((s, i) => {
    const sw = wave(t, s.k, s.ph) * s.amp + wave(t, 11, s.ph * 3) * 0.6, c = STRAND_C[i], a = Math.round(s.len / 3), b = Math.round(s.len * 2 / 3);
    ctx.drawImage(c, 0, 0, 3, a, s.x - 1 + Math.round(sw * 0.12), s.y0, 3, a);
    ctx.drawImage(c, 0, a, 3, b - a, s.x - 1 + Math.round(sw * 0.45), s.y0 + a, 3, b - a);
    ctx.drawImage(c, 0, b, 3, s.len - b, s.x - 1 + Math.round(sw), s.y0 + b, 3, s.len - b);
  });
}
function drawMaple(t) {
  blit(MAPLE_C);
  for (const m of MAPLE_TIPS) if (wave(t, m.k, m.ph) > 0.72) dot(m.x, m.y, m.col);           // leaves stirring at the crown's edge
}
function drawPromenade(t, crowd, pitch, billboard) {
  const DL = [], put = (fy, fn) => DL.push([fy, fn]);
  put(KIOSK.base, () => {
    halo(HL.hatch, KIOSK.cx, 178, 0.9);
    blit(KIOSK_C);
    if (here(RANK.vendor, crowd)) drawVendor(t);
    if (billboard) drawBoard(t, pitch);
  });
  for (const L of LAMPS) put(214, () => ctx.drawImage(LAMP_C, L.x - 4, L.y - 6));
  put(BENCH.y, () => { if (here(RANK.couple, crowd)) drawCouple(t); ctx.drawImage(BENCH_C, BENCH.x, BENCH.y - 13); });
  put(BIN.y, () => { ctx.fillStyle = 'rgba(8,10,24,0.32)'; ctx.fillRect(BIN.x - 6, BIN.y + 1, 12, 1); ctx.drawImage(BIN_C[binLid(t)], BIN.x - 5, BIN.y - 13); drawRaccoon(t); });
  if (here(RANK.friends, crowd)) put(QY + 1, () => drawFriends(t));
  if (here(RANK.photo, crowd)) put(EDGE + 1, () => drawPhoto(t));
  if (here(RANK.fisher, crowd)) put(EDGE + 1.2, () => drawFisher(t));
  if (here(RANK.kid, crowd)) put(EDGE + 2, () => drawKid(t));
  if (here(RANK.phone, crowd)) put(EDGE + 1.4, () => drawPhone(t));
  if (here(RANK.dog, crowd)) put(LANE_A, () => drawDogWalker(t));
  if (here(RANK.stroll, crowd)) put(LANE_A - 0.5, () => drawStroll(t));
  put(LANE_B, () => drawBikes(t, crowd));
  if (here(RANK.jog, crowd)) put(LANE_B + 0.5, () => drawJogger(t));
  LAWN_GEESE.forEach((g, i) => put(g.y, () => drawLawnGoose(g, i, t)));
  put(WILLOW.base, () => drawWillow(t));
  put(MAPLE.base, () => drawMaple(t));
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
  for (const L of LAMPS) { const I = 0.94 + 0.04 * wave(t, 900, L.x * 0.01); halo(HL.lamp, L.x, L.y + 2, I); halo(HL.lampCore, L.x, L.y, I); }
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the kiosk's sign; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out; crowd: the share of the
// people to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  drawBack(t);
  drawCN(t);
  drawFront(t);
  drawPlane(t);
  drawWater(t);
  drawFloaters(t);
  drawWaterGeese(t);
  drawTaxi(t);
  drawFerry(t, crowd);
  drawWash(t);
  drawSkein(t);
  if (wx.leaves) drawLeaves(LEAF_L[0], 0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  blit(GROUND_C);
  drawSkitter(t);
  if (wx.leaves) drawLeaves(LEAF_L[1], 1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawPromenade(t, crowd, pitch, billboard);
  if (wx.leaves) drawLeaves(LEAF_L[2], 2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'toronto',
  name: 'Toronto',
  tagline: 'Maple leaves and the CN Tower',
  timeZone: 'America/Toronto',
  accent: '#e00000',                                   // the maple leaf's red, written out: tools/seo/seo.mjs reads it from the source
  focusX: CN.x,
  landmark: [DOME.x0, CN.x + 8],                       // the Rogers Centre's dome and the CN Tower over it
  landmarkTop: CN.top,                                 // the antenna's tip
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: [
    { kind: 'landmark', x: CN.x - 6, y: CN.top, w: 13, h: 116 },
    { kind: 'window', x: HATCH.x0, y: HATCH.y0, w: HATCH.x1 - HATCH.x0 + 1, h: HATCH.y1 - HATCH.y0 + 1 },
    { kind: 'water', x: 206, y: 172, w: 80, h: 22 },
  ],
  ambience: { water: 0.35, wind: 0.25, traffic: 0.14, crowd: 0.05 },
  events: [
    { t: FERRY.horn, type: 'horn', x: 470, gain: 0.4 },
    { t: FERRY.horn + 0.5, type: 'boat', dur: 52, pan: [0.9, -0.9], gain: 0.3 },
    { t: TAXI[0].t0 + 0.5, type: 'boat', dur: 17, pan: [-0.9, 0.9], gain: 0.18 },
    { t: BIKES[0].bell, type: 'chime', x: 400, gain: 0.16 },
    { t: WASH.t0 + 1, type: 'splash', x: 260, gain: 0.22 },
    { t: WASH.t0 + 7, type: 'splash', x: 120, gain: 0.18 },
    { t: TAXI[1].t0 + 0.5, type: 'boat', dur: 17, pan: [0.9, -0.9], gain: 0.18 },
    { t: KISS[0] + 0.5, type: 'chime', x: 236, gain: 0.22 },
    { t: GEESE.land + 0.5, type: 'splash', x: 220, gain: 0.28 },
    { t: BIKES[1].bell, type: 'chime', x: 90, gain: 0.16 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
